/**
 * P2PAssistService - P2P辅助打洞服务
 * 当主连接为Relay时，后台尝试使用不同STUN服务器建立P2P连接
 */

import { eventBus } from './EventBus';
import { signalingService } from './SignalingService';

// STUN服务器列表（仅用于辅助打洞，不包含TURN）
// 自有 STUN 排在最前面，优先尝试
const ASSIST_STUN_SERVERS = [
  'stun:rtc.03kan.com:3400',    // 自有1
  'stun:rtc2.03kan.com:3400',   // 自有2
  'stun:rtc3.03kan.com:3400',   // 自有3
  'stun:stun.douyucdn.cn:18000',
  'stun:106.12.251.52:3478',
  'stun:106.12.251.31:3478',
  'stun:106.13.248.6:3478',
  'stun:106.13.249.54:3478',
  'stun:106.12.71.140:3478',
  'stun:106.12.251.193:3478',
  'stun:180.76.162.88:3478',
  'stun:stun4.l.google.com:19302',
];

// 步骤常量（Sync 打洞扩展：在 SDP 交换与 ICE 阶段之间插入 RTT/Sync）
export const Steps = {
  WEB_CREATE_PC: 1,
  WEB_CREATE_DC: 2,
  WEB_CREATE_OFFER: 3,
  WEB_SEND_OFFER: 4,
  GO_CREATE_PC: 5,
  GO_SET_REMOTE_DESC: 6,
  GO_CREATE_ANSWER: 7,
  GO_SEND_ANSWER: 8,
  WEB_SET_REMOTE_DESC: 9,
  // ----- Sync 打洞步骤（NAT 类型探测已移除，默认认为都可打洞）-----
  RTT_MEASURE: 17,          // 双端 ping-pong 测 RTT + 时钟偏差
  SYNC_NEGOTIATE: 18,       // 协商 fire_at、Host 端回 sync_ack
  SYNC_FIRE: 19,            // fire_at 时刻两端同时注入 buffered 候选
  // ----- 原有 ICE 阶段 -----
  ICE_GATHERING: 10,
  ICE_CONNECTING: 11,
  CHECK_CONNECTION_TYPE: 12,
  QUALITY_TEST_SEND: 13,
  QUALITY_TEST_RECV: 14,
  QUALITY_RESULT: 15,
  COMPLETE: 16,
};

// 步骤名称映射
export const StepNames: Record<number, string> = {
  [Steps.WEB_CREATE_PC]: '创建PeerConnection',
  [Steps.WEB_CREATE_DC]: '创建DataChannel',
  [Steps.WEB_CREATE_OFFER]: '创建Offer',
  [Steps.WEB_SEND_OFFER]: '发送Offer到GO',
  [Steps.GO_CREATE_PC]: '创建PeerConnection',
  [Steps.GO_SET_REMOTE_DESC]: '设置RemoteDescription',
  [Steps.GO_CREATE_ANSWER]: '创建Answer',
  [Steps.GO_SEND_ANSWER]: '发送Answer到WEB',
  [Steps.WEB_SET_REMOTE_DESC]: '设置RemoteDescription',
  [Steps.RTT_MEASURE]: 'RTT测量',
  [Steps.SYNC_NEGOTIATE]: 'Sync协商',
  [Steps.SYNC_FIRE]: 'Sync点火',
  [Steps.ICE_GATHERING]: 'ICE候选收集',
  [Steps.ICE_CONNECTING]: 'ICE连接建立',
  [Steps.CHECK_CONNECTION_TYPE]: '检测连接类型',
  [Steps.QUALITY_TEST_SEND]: '质量测试发送',
  [Steps.QUALITY_TEST_RECV]: '质量测试接收',
  [Steps.QUALITY_RESULT]: '结果计算',
  [Steps.COMPLETE]: '完成',
};

// 步骤所属端
export const StepSides: Record<number, 'web' | 'go' | 'both'> = {
  [Steps.WEB_CREATE_PC]: 'web',
  [Steps.WEB_CREATE_DC]: 'web',
  [Steps.WEB_CREATE_OFFER]: 'web',
  [Steps.WEB_SEND_OFFER]: 'web',
  [Steps.GO_CREATE_PC]: 'go',
  [Steps.GO_SET_REMOTE_DESC]: 'go',
  [Steps.GO_CREATE_ANSWER]: 'go',
  [Steps.GO_SEND_ANSWER]: 'go',
  [Steps.WEB_SET_REMOTE_DESC]: 'web',
  [Steps.RTT_MEASURE]: 'both',
  [Steps.SYNC_NEGOTIATE]: 'both',
  [Steps.SYNC_FIRE]: 'both',
  [Steps.ICE_GATHERING]: 'both',
  [Steps.ICE_CONNECTING]: 'both',
  [Steps.CHECK_CONNECTION_TYPE]: 'go',
  [Steps.QUALITY_TEST_SEND]: 'go',
  [Steps.QUALITY_TEST_RECV]: 'web',
  [Steps.QUALITY_RESULT]: 'web',
  [Steps.COMPLETE]: 'both',
};

// Sync 状态（每个 session 一份，用于 UI 展示）
// NAT 类型分型已移除：默认认为都可打洞，交给 ICE 自己决定
export interface SyncInfo {
  rttMedianMs?: number;
  rttSamples?: number[];
  clockOffsetMs?: number;        // Host steady_clock - browser performance.now() (估算)
  fireAtMs?: number;             // 浏览器本地 performance.now() 的目标时刻
  firedAtMs?: number;            // 实际点火时刻
  syncAccuracyMs?: number;       // 实际点火 - fireAtMs（浏览器自己的抖动）
  // 诊断：双端 fire 偏差（Host 实际 fire 时刻 - 浏览器实际 fire 时刻，映射到同一时钟）
  // 正值：Host 比浏览器晚 fire；负值：Host 比浏览器早 fire
  syncDriftMs?: number;
  phase?: 'rtt' | 'sync' | 'fired' | 'ice' | 'quality' | 'done';
  holePunchMethod?: 'sync' | 'fallback_immediate' | 'legacy';
}

// ==================== 打洞报告：诊断数据类型 ====================
// 单个 ICE candidate 的解析信息（local 或 remote）
export interface CandidateInfo {
  type: 'host' | 'srflx' | 'prflx' | 'relay' | 'unknown';
  protocol: 'udp' | 'tcp' | 'unknown';
  address: string;
  port: number;
  relatedAddress?: string;   // srflx/prflx/relay 才有
  relatedPort?: number;
  url?: string | null;       // 来源 STUN URL：本地候选可有，远端候选除非 Host 上报否则 null
  priority?: number;
  foundation?: string;
  raw?: string;              // 原始 candidate 字符串（debug 用）
}

// onicecandidateerror 事件采集
export interface CandidateErrorInfo {
  atMs: number;              // 相对 attempt.startTime 的毫秒数
  url?: string;
  address?: string;
  port?: number;
  errorCode: number;
  errorText: string;
  hostCandidate?: string;
}

// candidate-pair stats 摘要（pc.getStats 提取）
export interface PairInfo {
  localType: string;          // host/srflx/relay
  remoteType: string;
  localAddress?: string;
  localPort?: number;
  remoteAddress?: string;
  remotePort?: number;
  state: string;              // waiting/in-progress/succeeded/failed
  nominated?: boolean;
  priority?: number;
  requestsSent?: number;
  requestsReceived?: number;
  responsesSent?: number;
  responsesReceived?: number;
  currentRoundTripTimeMs?: number;
  bytesSent?: number;
  bytesReceived?: number;
}

export interface SelectedPairInfo extends PairInfo {
  localUrl?: string | null;   // selected pair 的 local candidate STUN url（仅本地候选有）
}

// session 终结时的 stats 快照
export interface FinalStatsSnapshot {
  selectedPair: SelectedPairInfo | null;
  allPairs: PairInfo[];
}

// 信令时序时间戳（performance.now 相对值，ms）
export interface SignalingTimings {
  offerCreatedAt?: number;
  offerSentAt?: number;
  answerReceivedAt?: number;
  setRemoteAt?: number;
}

// 候选按类型计数（验证 fire 时刻 buffer 是否包含 srflx 候选）
export interface CandidateTypeCounts {
  host: number;
  srflx: number;
  prflx: number;
  relay: number;
  unknown: number;
  total: number;
}

// Host 视角的 selectedPair 摘要（来自 host_stats 信令上报，每 500ms 刷新）
export interface HostStatsInfo {
  state: string;                  // succeeded / in-progress / etc
  nominated: boolean;
  localType: string;
  localAddress: string;
  localPort: number;
  localUrl?: string;              // STUN url（仅 srflx 候选有）
  remoteType: string;
  remoteAddress: string;
  remotePort: number;
  requestsSent: number;
  requestsReceived: number;
  responsesSent: number;
  responsesReceived: number;
}

// session 范围内采集的全部诊断数据
export interface SessionDiagnostics {
  signalingTimings: SignalingTimings;
  localCandidates: CandidateInfo[];
  remoteCandidates: CandidateInfo[];
  candidateErrors: CandidateErrorInfo[];
  iceStateTransitions: Array<{ atMs: number; state: string }>;
  gatheringStateTransitions: Array<{ atMs: number; state: string }>;
  peerConnectionState?: string;
  dataChannelError?: { detail?: string; message?: string; channelLabel?: string };
  finalStats?: FinalStatsSnapshot;
  // attempt 终结时从 session.syncInfo 浅拷贝；session 销毁后这是仅存的 sync 数据快照
  syncSnapshot?: SyncInfo;
  // 方案 M 诊断字段：验证"fire 时 buffer 不含远端 srflx → fire 后才到达"假设
  // fire 触发时 bufferedHostCandidates 按类型分布
  fireBufferStats?: CandidateTypeCounts;
  // fire 之后到达的远端候选累加（同一 attempt 内）
  postFireRemoteCandidates?: CandidateTypeCounts;
  // Host 视角的实时候选对统计（来自 Host 通过主连接 ctrl DC 上报的 host_stats）
  hostFinalStats?: HostStatsInfo;
}

// attempt 终结时的诊断结论（11 条规则的输出，含"中止"用于未真正运行就被清的 attempt）
export type RootCategory =
  | '成功' | '中止' | '信令错误' | 'Sync探测失败' | 'STUN不可达' | 'STUN错误'
  | '远端STUN不可达' | 'NAT阻断' | 'DTLS失败' | 'SCTP失败'
  | '链路质量差' | 'ICE超时' | '进行中' | '待开始' | '未知';

export interface AttemptDiagnosis {
  根因分类: RootCategory;
  一句话结论: string;
  失败步骤号?: number;
  失败步骤名?: string;
  失败端?: 'web' | 'go';
  原始错误信息?: string | null;
  关键证据: Record<string, unknown>;
}

// 步骤状态
export interface StepStatus {
  step: number;
  name: string;
  side: 'web' | 'go' | 'both';
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  startTime?: number;
  endTime?: number;
  error?: string;
  details?: string;
}

// STUN尝试记录
export interface StunAttempt {
  index: number;
  stunServers: string[];  // 支持多个STUN服务器
  sessionId: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  steps: StepStatus[];
  startTime: number;
  endTime?: number;
  result?: {
    connectionType: string;
    throughputKbps?: number;
    rttMs?: number;
    localCandidate?: string;
    remoteCandidate?: string;
    totalTimeMs: number;
  };
  failedStep?: number;
  failedSide?: 'web' | 'go';
  failedReason?: string;
  // 建立后断开标记（attempt.status 仍保持 'success'，单独标记掉线，用于"被过滤的 P2P 列表"）
  droppedAfterEstablished?: boolean;
  droppedReason?: string;     // 'ICE disconnected' / 'ICE failed' 等
  droppedAt?: number;         // 断开时刻 ms（epoch）
  // 质量测试结束后写入：false 表示 P2P 明显优于 TURN（触发早停 + 切换）
  skipSwitch?: boolean;
  webCandidates: string[];
  goCandidates: string[];
  // 打洞报告：每个 attempt 的完整诊断数据（在 session 生命周期内更新；session 终结时调用 diagnose 填 diagnosis）
  diagnostics?: SessionDiagnostics;
  diagnosis?: AttemptDiagnosis;
  round?: number;             // 该 attempt 所属轮次（创建时填）
}

// "被过滤的 P2P"列表条目（service 端派生，UI 直接渲染，避免双端各算一遍漂移）
export interface FilteredAttempt {
  attempt: StunAttempt;                                            // 原 attempt 引用
  filterReason: string;                                            // 给用户看的中文原因
  filterCategory: 'dropped' | 'quality' | 'signal' | 'ice' | 'sync' | 'other';
  filteredAt: number;                                              // 失败/掉线时刻 ms
}

// 资源清理模式（finalizeResources 使用）
enum FinalizeMode {
  // 找到最佳连接后：保留 best session/established 用于后续切换，清理其他所有资源
  KeepBestForSwitch = 'keepBestForSwitch',
  // 一轮失败，准备开新一轮：清所有资源 + bestAttempt，但保留 TURN RTT/loss 历史
  DiscardAllKeepTurnHistory = 'discardAllKeepTurnHistory',
  // 完全停止：stop/reject/切换完成后，彻底归零（含 TURN 历史）
  DiscardAll = 'discardAll',
}

// 日志条目
export interface LogEntry {
  timestamp: number;
  side: 'web' | 'go';
  level: 'info' | 'success' | 'warning' | 'error';
  step?: number;
  message: string;
}

// 质量测试数据
interface QualityTestData {
  startTime: number;
  bytesReceived: number;
  packetsReceived: Set<number>;  // 收到的包序号
  totalPackets: number;          // 总包数
}

// 辅助会话
interface AssistSession {
  id: string;
  stunServers: string[];  // 支持多个STUN服务器
  pc: RTCPeerConnection | null;
  dataChannel: RTCDataChannel | null;
  qualityChannel: RTCDataChannel | null;  // quality-test通道（不可靠，用于丢包检测）
  inputChannel: RTCDataChannel | null;  // input通道（用于切换后的键盘控制）
  mouseChannel: RTCDataChannel | null;  // mouse通道（用于切换后的鼠标控制）
  fileCtrlChannel: RTCDataChannel | null;  // file-ctrl通道（用于切换后的文件控制）
  fileDataChannel: RTCDataChannel | null;  // file-data通道（用于切换后的文件数据传输）
  attempt: StunAttempt;
  qualityTest: QualityTestData | null;
  connectTimeout: number | null;
  qualityTestTimeout: number | null;  // 质量测试超时
  answerReceived: boolean;
  remoteStream: MediaStream | null;  // 接收的视频流，用于切换
  // ----- Sync 打洞 -----
  syncMode: boolean;                                 // 是否启用 sync 模式（默认 true）
  bufferedHostCandidates: RTCIceCandidateInit[];     // fire_at 之前暂存 Host 的候选
  syncFireTimer: number | null;                      // setTimeout 句柄
  syncInfo: SyncInfo;                                // UI 展示用
  rttProbes: Map<number, { tSendMs: number; tRecvMs?: number; tHostRecvMs?: number; tHostSendMs?: number }>;
  syncAckReceived: boolean;
  // 诊断：sync_negotiate 发送时刻（performance.now()），用于配合 sync_ack 计算时钟偏差
  syncNegotiateSentAtMs?: number;
  // ----- 质量测试完成标记 -----
  // completeQualityTest 入口立即置 true。防止 handleQualityPacket 收到迟到包时重新初始化 qualityTest
  // 导致二次结算 + 双份 quality_result 发给 Host（Host 可能先收到错误的 failed 结果再收到正确结果）。
  qualityTestCompleted: boolean;
  // ----- 实时 stats 轮询定时器 -----
  // 每 500ms 调 collectFinalStats 把当前 selectedPair / sent / recv 写入 diagnostics.finalStats，
  // 让 panel 在 attempt 跑中途也能实时显示候选对详情。attempt 终结时由 cleanupSession 清掉。
  statsPollTimer: number | null;
}

// 已建立的P2P连接信息
export interface EstablishedP2PConnection {
  sessionId: string;
  stunServers: string[];  // 支持多个STUN服务器
  connectionType: string;
  throughputKbps: number;
  rttMs?: number;
  localCandidate?: string;
  remoteCandidate?: string;
  establishedAt: number;
  iceState: string;
  pc: RTCPeerConnection;
  hasVideo: boolean;
  hasAudio: boolean;
  // ===== 累积选优架构新增字段 =====
  ready: boolean;           // 视频轨是否已就绪（收到 Host switch_ready 后置 true，可立即切换）
  acceptedAt: number;       // 加入 established 的时间戳（次级排序：吞吐 RTT 都相同时先到者优）
  attempt: StunAttempt;     // 对应 StunAttempt 引用，用于派生 bestAttempt
}

// 待用户确认的切换上下文（弹窗显示期间持有）
interface PendingSwitchContext {
  sessionId: string;
  pc: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  inputChannel: RTCDataChannel | null;
  mouseChannel: RTCDataChannel | null;
  remoteStream: MediaStream;
  stunServers: string[];
  connectionType: string;
}

class P2PAssistService {
  private isRunning = false;
  private currentAttemptIndex = 0;
  private sessions: Map<string, AssistSession> = new Map();
  private establishedConnections: Map<string, EstablishedP2PConnection> = new Map();
  private bestAttempt: StunAttempt | null = null;
  private logs: LogEntry[] = [];
  private attempts: StunAttempt[] = [];
  // 打洞报告：跨轮累积的全部 attempt（与 attempts 共用引用，但不会被 startNewRound 清空）
  // 仅在 start() 入口和 stop() 时清空。切换成功后保留，供"打洞报告"按钮读取。
  private attemptHistory: StunAttempt[] = [];
  private retryTimer: number | null = null;
  private switchReadyTimeout: number | null = null;
  private mainConnectionType: 'p2p' | 'relay' | 'unknown' = 'unknown';
  private roundCount = 0; // 当前轮数
  private forceRelayMode = false; // 强制中转模式时禁用辅助打洞
  // 本次连接生命周期内 P2P Assist 是否已"消费"（弹过窗一次后置 true，断线重连重置）
  private consumed = false;
  // 弹窗显示期间持有的待切换上下文
  private pendingSwitch: PendingSwitchContext | null = null;
  // 当前 turn 连接的 RTT / loss 历史（最近 5 次样本），用于与 P2P 质量对比判断是否"更优"
  private turnRttHistory: number[] = [];
  private turnLossHistory: number[] = [];
  private static readonly TURN_STATS_HISTORY_SIZE = 5;

  // 配置
  private readonly CONNECT_TIMEOUT = 3000; // 3秒连接超时
  private readonly QUALITY_TEST_TIMEOUT = 3000; // 3秒质量测试超时
  private readonly STUN_RETRY_DELAY = 0; // 立即尝试下一个（成功后等3秒）
  private readonly MAX_ROUNDS = 20; // 最大尝试轮数
  private readonly BASE_RETRY_DELAY = 5000; // 基础重试延迟（5秒，递增）

  // ===== 累积选优 & 阈值自动切换 =====
  private readonly AUTO_SWITCH_THRESHOLD = 3;        // established 达到此数量自动切换到最优
  private readonly PREPARE_TIMEOUT_MS = 3000;        // prepare → switch_ready 超时
  private readonly TOAST_DEBOUNCE_MS = 1000;         // 气泡 debounce
  // 当前等待 Host switch_ready 回复的 sessionId（竞争保护：仅处理匹配的 switch_ready）
  private pendingPrepareSessionId: string | null = null;
  private prepareTimeoutTimer: number | null = null;
  // 气泡 debounce 合并
  private notifyToastTimer: number | null = null;
  private notifyPendingCount = 0;

  // ===== 首条 established 强制切换倒计时 =====
  // 第一条打通后若 60s 内未凑满 AUTO_SWITCH_THRESHOLD，强行用当前最优切换（避免一直等第 3 条）
  private readonly FORCE_SWITCH_TIMEOUT_MS = 60000;
  private firstEstablishedAt: number | null = null;  // 首条 established 的时间戳（UI 倒计时用）
  private firstEstablishedCountdownTimer: number | null = null;

  constructor() {
    // 监听辅助打洞相关事件
    eventBus.on('p2p_assist:answer', this.handleAnswer.bind(this));
    eventBus.on('p2p_assist:candidate', this.handleCandidate.bind(this));
    eventBus.on('p2p_assist:step_update', this.handleStepUpdate.bind(this));
    eventBus.on('p2p_assist:quality_progress', this.handleQualityProgress.bind(this));
    eventBus.on('p2p_assist:renegotiate_offer', this.handleRenegotiateOffer.bind(this));
    eventBus.on('p2p_assist:switch_ready', this.handleSwitchReady.bind(this));

    // Sync 打洞控制消息（来自 relay PC 的 data DC，由 WebRTCService 转发）
    eventBus.on('p2p_assist:ctrl_recv', this.handleCtrlMessage.bind(this));

    // Host 送来的 winning candidate 信息（用于"已建立 P2P"列表展示服务端 STUN + 外网地址）
    eventBus.on('p2p_assist:host_candidate_info', this.handleHostCandidateInfo.bind(this));

    // 监听主连接状态
    eventBus.on('stats:update', this.checkMainConnectionType.bind(this));

    // 监听 icePolicy 变更
    eventBus.on('config:icePolicyChange', this.handleIcePolicyChange.bind(this));

    // 监听连接状态：断线/失败时重置 consumed 标志，允许下次连接再次启用 P2P Assist
    eventBus.on('connection:stateChange', this.handleConnectionStateChange.bind(this));
  }

  // ==================== Sync 打洞协议 ====================
  //
  // 原理：浏览器和 Host 正常完成 SDP 交换（setRemoteDescription 立即执行），
  // 但 **远端 ICE 候选** 在两端都不立即 addIceCandidate，而是应用层 buffer。
  // 通过 relay PC 的 data DC 测量 RTT + 协商 fire_at 时刻，
  // fire_at 到达时两端同时 addIceCandidate，ICE agent 从这一刻才发首个连通性检查。
  // 达成 DCUtR 风格的"两端首包几乎同时到达对方 NAT"。

  // 通过 WebRTCService 暴露的 data DC 发送控制消息
  private sendCtrl(payload: Record<string, unknown>): void {
    eventBus.emit('p2p_assist:ctrl_send', payload);
  }

  // ==================== 打洞报告：诊断采集工具 ====================

  // 取 attempt.diagnostics（保证非空）
  private getDiag(attempt: StunAttempt): SessionDiagnostics {
    if (!attempt.diagnostics) {
      attempt.diagnostics = {
        signalingTimings: {},
        localCandidates: [],
        remoteCandidates: [],
        candidateErrors: [],
        iceStateTransitions: [],
        gatheringStateTransitions: [],
      };
    }
    return attempt.diagnostics;
  }

  // 相对 attempt 起始时刻的 ms
  private msFromStart(attempt: StunAttempt): number {
    return attempt.startTime > 0 ? Date.now() - attempt.startTime : 0;
  }

  // 解析 candidate 字符串 + RTCIceCandidate 对象，返回 CandidateInfo
  // 标准 candidate: "candidate:<foundation> <component> <protocol> <priority> <address> <port> typ <type> [raddr <addr> rport <port>] ..."
  private parseCandidate(c: RTCIceCandidate | RTCIceCandidateInit | null | undefined): CandidateInfo | null {
    if (!c) return null;
    const raw = (c as RTCIceCandidate).candidate ?? (c as RTCIceCandidateInit).candidate ?? '';
    if (!raw) return null;
    const parts = raw.split(/\s+/);
    if (parts.length < 8) return { type: 'unknown', protocol: 'unknown', address: '', port: 0, raw };
    // parts[0]="candidate:foundation", [1]=component, [2]=protocol, [3]=priority, [4]=address, [5]=port, [6]="typ", [7]=type
    const foundation = parts[0].split(':')[1] || '';
    const protoLow = (parts[2] || '').toLowerCase();
    const protocol: CandidateInfo['protocol'] = protoLow === 'udp' ? 'udp' : protoLow === 'tcp' ? 'tcp' : 'unknown';
    const priority = parseInt(parts[3], 10) || undefined;
    const address = parts[4] || '';
    const port = parseInt(parts[5], 10) || 0;
    const typ = parts[7] || 'unknown';
    const type: CandidateInfo['type'] =
      typ === 'host' || typ === 'srflx' || typ === 'prflx' || typ === 'relay' ? typ : 'unknown';
    let relatedAddress: string | undefined;
    let relatedPort: number | undefined;
    for (let i = 8; i < parts.length - 1; i++) {
      if (parts[i] === 'raddr') relatedAddress = parts[i + 1];
      if (parts[i] === 'rport') relatedPort = parseInt(parts[i + 1], 10) || undefined;
    }
    // RTCIceCandidate 标准字段（仅本地候选有 url）
    const url = (c as RTCIceCandidate & { url?: string }).url ?? null;
    return { type, protocol, address, port, relatedAddress, relatedPort, url, priority, foundation, raw };
  }

  // session 终结时调一次：从 pc.getStats 提取 selectedPair + allPairs，写入 diagnostics.finalStats
  private async collectFinalStats(session: AssistSession): Promise<void> {
    if (!session.pc) return;
    const diag = this.getDiag(session.attempt);
    if (diag.finalStats) return; // 已采集过
    try {
      const report = await session.pc.getStats();
      const candidatesById = new Map<string, RTCStats & { address?: string; port?: number; candidateType?: string; url?: string }>();
      report.forEach((s) => {
        if (s.type === 'local-candidate' || s.type === 'remote-candidate') {
          candidatesById.set(s.id, s as RTCStats & { address?: string; port?: number; candidateType?: string; url?: string });
        }
      });
      const allPairs: PairInfo[] = [];
      let selectedPair: SelectedPairInfo | null = null;
      report.forEach((s) => {
        if (s.type !== 'candidate-pair') return;
        const p = s as RTCIceCandidatePairStats;
        const local = p.localCandidateId ? candidatesById.get(p.localCandidateId) : undefined;
        const remote = p.remoteCandidateId ? candidatesById.get(p.remoteCandidateId) : undefined;
        const pair: PairInfo = {
          localType: local?.candidateType || 'unknown',
          remoteType: remote?.candidateType || 'unknown',
          localAddress: local?.address,
          localPort: local?.port,
          remoteAddress: remote?.address,
          remotePort: remote?.port,
          state: p.state || 'unknown',
          nominated: p.nominated,
          priority: p.priority,
          requestsSent: p.requestsSent,
          requestsReceived: p.requestsReceived,
          responsesSent: p.responsesSent,
          responsesReceived: p.responsesReceived,
          currentRoundTripTimeMs: typeof p.currentRoundTripTime === 'number' ? p.currentRoundTripTime * 1000 : undefined,
          bytesSent: p.bytesSent,
          bytesReceived: p.bytesReceived,
        };
        allPairs.push(pair);
        if (p.nominated && p.state === 'succeeded') {
          selectedPair = { ...pair, localUrl: local?.url ?? null };
        }
      });
      // selectedPair 兜底：没 nominated/succeeded 时取优先级最高的（localUrl 拿不到，置 null）
      if (!selectedPair && allPairs.length > 0) {
        const sorted = [...allPairs].sort((a, b) => (b.priority || 0) - (a.priority || 0));
        selectedPair = { ...sorted[0], localUrl: null };
      }
      diag.finalStats = { selectedPair, allPairs };
    } catch (e) {
      this.log('web', 'warning', undefined, `collectFinalStats 失败: ${(e as Error).message}`);
    }
  }

  // PC 创建后立即挂诊断回调（独立于业务回调，纯采集）
  private attachDiagnosticHooks(pc: RTCPeerConnection, attempt: StunAttempt): void {
    const diag = this.getDiag(attempt);
    // 1) onicecandidateerror
    (pc as RTCPeerConnection & { onicecandidateerror?: (e: Event) => void }).onicecandidateerror = (e: Event) => {
      const ev = e as RTCPeerConnectionIceErrorEvent;
      diag.candidateErrors.push({
        atMs: this.msFromStart(attempt),
        url: ev.url,
        address: ev.address || undefined,
        port: ev.port || undefined,
        errorCode: ev.errorCode,
        errorText: ev.errorText,
        hostCandidate: ev.hostCandidate || undefined,
      });
      this.log('web', 'warning', Steps.ICE_GATHERING,
        `ICE 候选错误 [${ev.errorCode}] ${ev.url || ''}: ${ev.errorText}`);
    };
    // 2) onicegatheringstatechange
    pc.onicegatheringstatechange = () => {
      diag.gatheringStateTransitions.push({ atMs: this.msFromStart(attempt), state: pc.iceGatheringState });
    };
    // 3) onconnectionstatechange（DTLS/连接最终态）
    pc.onconnectionstatechange = () => {
      diag.peerConnectionState = pc.connectionState;
    };
  }

  // 失败诊断：根据 attempt + diagnostics 数据，按规则确定根因分类，写入 attempt.diagnosis
  // 不依赖任何"建议性"语言，只输出可被肉眼复核的事实
  private diagnose(attempt: StunAttempt): void {
    const diag = this.getDiag(attempt);
    const stunErrCount = diag.candidateErrors.length;
    const localSrflx = diag.localCandidates.filter(c => c.type === 'srflx');
    const remoteSrflx = diag.remoteCandidates.filter(c => c.type === 'srflx');
    const hasLocalSrflx = localSrflx.length > 0;
    const hasRemoteSrflx = remoteSrflx.length > 0;
    const sp = diag.finalStats?.selectedPair || null;
    const evidence: Record<string, unknown> = {
      本地有srflx: hasLocalSrflx,
      远端有srflx: hasRemoteSrflx,
      本地候选数: diag.localCandidates.length,
      远端候选数: diag.remoteCandidates.length,
      STUN错误数: stunErrCount,
      ICE最终状态: diag.iceStateTransitions.at(-1)?.state ?? null,
      PC最终状态: diag.peerConnectionState ?? null,
      候选对发出请求数: sp?.requestsSent ?? null,
      候选对收到响应数: sp?.responsesReceived ?? null,
      DataChannel错误: diag.dataChannelError?.detail ?? null,
    };

    // 成功路径
    if (attempt.status === 'success') {
      const r = attempt.result;
      const summary = r
        ? `ICE 直连建立，吞吐 ${r.throughputKbps ?? '-'} kbps，类型 ${r.connectionType}` +
          (r.rttMs !== undefined ? `，RTT ${Math.round(r.rttMs)}ms` : '')
        : 'ICE 直连建立';
      attempt.diagnosis = {
        根因分类: '成功',
        一句话结论: summary,
        失败步骤号: undefined,
        失败步骤名: undefined,
        失败端: undefined,
        原始错误信息: null,
        关键证据: evidence,
      };
      return;
    }

    const fStep = attempt.failedStep;
    const fName = fStep !== undefined ? StepNames[fStep] : undefined;
    const reason = attempt.failedReason || '';
    const side = attempt.failedSide;

    // 中止分支：finalizeResources 把 pending → failed 时 reason='aborted' 或 'early_stop:*'。
    // 这种 attempt 从未真正运行（无候选/无 stats），不应走 STUN/NAT 诊断。
    if (reason === 'aborted' || reason.startsWith('early_stop')) {
      attempt.diagnosis = {
        根因分类: '中止',
        一句话结论: reason === 'aborted'
          ? 'attempt 未运行就被中止（停止打洞/切换/页面关闭）'
          : `attempt 因 ${reason} 跳过（本轮已找到 winner）`,
        失败步骤号: undefined,
        失败步骤名: undefined,
        失败端: undefined,
        原始错误信息: reason,
        关键证据: { 是否运行过: false, 本地候选数: diag.localCandidates.length },
      };
      return;
    }

    let category: RootCategory = '未知';
    let summary = reason || '未提供失败原因';

    // 规则按优先级判
    const dcDetail = diag.dataChannelError?.detail || '';
    const peerState = diag.peerConnectionState;
    const lastIceState = diag.iceStateTransitions.at(-1)?.state;

    if (fStep !== undefined && [Steps.WEB_CREATE_PC, Steps.WEB_CREATE_DC, Steps.WEB_CREATE_OFFER, Steps.WEB_SEND_OFFER, Steps.GO_CREATE_PC, Steps.GO_SET_REMOTE_DESC, Steps.GO_CREATE_ANSWER, Steps.GO_SEND_ANSWER, Steps.WEB_SET_REMOTE_DESC].includes(fStep)) {
      category = '信令错误';
      summary = `${fName || '信令步骤'} 失败：${reason}（属于 Offer/Answer/SetRemote 阶段，多为 SDP 或代码层问题）`;
    } else if (fStep === Steps.RTT_MEASURE) {
      category = 'Sync探测失败';
      summary = `RTT 探测失败：${reason}（relay PC 的 data DC 未通，多为信令转发或 ctrl 路由代码问题）`;
    } else if (dcDetail === 'sctp-failure') {
      category = 'SCTP失败';
      summary = `SCTP 层失败 (${dcDetail})：${reason}`;
    } else if (dcDetail === 'dtls-failure' || (peerState === 'failed' && lastIceState === 'connected')) {
      category = 'DTLS失败';
      summary = `DTLS 握手失败 (PC=${peerState}, ICE=${lastIceState})：${reason}（多为证书或代码层问题）`;
    } else if (!hasLocalSrflx && stunErrCount > 0) {
      category = 'STUN错误';
      const errs = diag.candidateErrors.map(e => `${e.errorCode} ${e.errorText}`).slice(0, 3).join('；');
      summary = `本地无 srflx 候选，STUN 服务返回错误：${errs}`;
    } else if (!hasLocalSrflx) {
      category = 'STUN不可达';
      summary = `本地未收集到 srflx 候选（共 ${diag.localCandidates.length} 个，均为 ${diag.localCandidates.map(c => c.type).join('/') || '无'}）— 客户端到 STUN 服务不通（UDP 被封 / STUN 服务挂 / 配置错）`;
    } else if (hasLocalSrflx && !hasRemoteSrflx) {
      category = '远端STUN不可达';
      summary = `远端未送来 srflx 候选（共 ${diag.remoteCandidates.length} 个，均为 ${diag.remoteCandidates.map(c => c.type).join('/') || '无'}）— Host 端到 STUN 服务不通`;
    } else if (sp && (sp.requestsSent ?? 0) > 0 && (sp.responsesReceived ?? 0) === 0) {
      category = 'NAT阻断';
      summary = `双端 srflx 候选齐全但 connectivity check 无响应（发出 ${sp.requestsSent} 次 binding request，收到 0 次响应）— 真·NAT 无法直连（对称 NAT / UDP 被防火墙封）`;
    } else if (fStep !== undefined && [Steps.QUALITY_TEST_SEND, Steps.QUALITY_TEST_RECV, Steps.QUALITY_RESULT].includes(fStep)) {
      category = '链路质量差';
      const r = attempt.result;
      const detail = r ? `吞吐 ${r.throughputKbps ?? '-'} kbps` : '';
      summary = `ICE 已通但质量未达标：${reason}${detail ? `（${detail}）` : ''}`;
    } else if (fStep === Steps.ICE_CONNECTING) {
      category = 'ICE超时';
      summary = `ICE 连接超时（最终状态 ${lastIceState || 'unknown'}）：${reason}`;
    } else {
      category = '未知';
      summary = reason || `步骤 ${fStep ?? '?'} ${fName ?? ''} 失败原因待确认`;
    }

    attempt.diagnosis = {
      根因分类: category,
      一句话结论: summary,
      失败步骤号: fStep,
      失败步骤名: fName,
      失败端: side,
      原始错误信息: reason || null,
      关键证据: evidence,
    };
  }

  // 在 attempt 终结时调用：先采集 finalStats，再 diagnose（先后顺序保证 diagnose 能看到 selectedPair）
  // fire-and-forget，调用方不必 await
  private async finalizeAttemptDiagnostics(session: AssistSession): Promise<void> {
    try {
      await this.collectFinalStats(session);
    } catch { /* swallow */ }
    // 把 session.syncInfo 快照到 attempt.diagnostics（session 销毁后仍可读）
    const diag = this.getDiag(session.attempt);
    if (!diag.syncSnapshot && session.syncInfo) {
      diag.syncSnapshot = { ...session.syncInfo };
    }
    this.diagnose(session.attempt);
    this.emitStateChange();
  }

  // ==================== 打洞报告：buildReport（精简版） ====================
  // 只输出对"找历史 + 失败根因 + 成功信息"有用的字段，砍掉所有调试细节。
  // 设计目标：≤300 行 / ≤15KB，扫一眼就能判断每个 attempt 的根因。
  buildReport(): Record<string, unknown> {
    const formatStun = (s: string | null | undefined): string => (s || '').replace(/^stun:/, '') || '?';
    // candidate → "addr:port  ← STUN" 字符串（仅 srflx 才有 url；不带 url 的就只显示 addr:port）
    const candLine = (c: CandidateInfo): string =>
      c.url ? `${c.address}:${c.port}  ← ${formatStun(c.url)}` : `${c.address}:${c.port}`;
    // 状态映射
    const statusZh = (s: StunAttempt['status']): string =>
      s === 'success' ? '成功' : s === 'failed' ? '失败' : s === 'running' ? '进行中' : '待开始';

    const attempts = this.attemptHistory;
    const successCount = attempts.filter(a => a.status === 'success').length;
    const failCount = attempts.filter(a => a.status === 'failed').length;
    const distribution: Record<string, number> = {};
    for (const a of attempts) {
      let cat = a.diagnosis?.根因分类;
      if (!cat) {
        if (a.status === 'success') cat = '成功';
        else if (a.status === 'running') cat = '进行中';
        else if (a.status === 'pending') cat = '待开始';
        else cat = '未知';
      }
      distribution[cat] = (distribution[cat] || 0) + 1;
    }

    return {
      报告版本: '1.0',
      生成时间: new Date().toISOString(),
      汇总: {
        当前轮次: this.roundCount,
        最大轮次: this.MAX_ROUNDS,
        尝试总数: attempts.length,
        成功数: successCount,
        失败数: failCount,
        已建立P2P数: this.establishedConnections.size,
        根因分布: distribution,
        TURN基准: {
          平均RTT_ms: this.getTurnAvgRtt(),
          平均丢包率: this.getTurnAvgLoss(),
        },
      },
      尝试列表: attempts.map(a => {
        const diag = a.diagnostics;
        const r = a.result;

        // 选中候选对：扁平化为一行字符串
        let selectedPairStr: string | undefined;
        const sp = diag?.finalStats?.selectedPair;
        if (sp) {
          const localStr = `${sp.localAddress ?? '?'}:${sp.localPort ?? '?'} ${sp.localType}`;
          const remoteStr = `${sp.remoteAddress ?? '?'}:${sp.remotePort ?? '?'} ${sp.remoteType}`;
          const stats = (sp.state === 'succeeded' && typeof sp.currentRoundTripTimeMs === 'number')
            ? `succeeded · RTT=${Math.round(sp.currentRoundTripTimeMs)}ms`
            : `${sp.state} · sent=${sp.requestsSent ?? 0} recv=${sp.responsesReceived ?? 0}`;
          selectedPairStr = `${localStr} ⇄ ${remoteStr} · ${stats}`;
        }

        // 本地/远端公网（只取 srflx）
        const localPublic = diag?.localCandidates
          .filter(c => c.type === 'srflx')
          .map(candLine);
        const remotePublic = diag?.remoteCandidates
          .filter(c => c.type === 'srflx')
          .map(candLine);

        // STUN 错误（标注是 IPv4 还是 IPv6 触发的，address 是本地 host candidate 的地址）
        const ipFamily = (addr?: string): string => {
          if (!addr) return '未知';
          if (addr.endsWith('.local')) return 'mDNS';      // mDNS hostname
          if (addr.includes(':')) return 'IPv6';            // 含冒号 = IPv6
          if (/^\d+\.\d+\.\d+\.\d+$/.test(addr)) return 'IPv4';
          return '未知';
        };
        const stunErrors = (diag?.candidateErrors.length ?? 0) > 0
          ? diag!.candidateErrors.map(e =>
              `[${e.errorCode}/${ipFamily(e.address)}] ${formatStun(e.url)}  ${e.errorText}`)
          : undefined;

        // 根因 + 结论：默认走 diagnosis；进行中/待开始单独处理
        let category: string;
        let summary: string;
        if (a.diagnosis) {
          category = a.diagnosis.根因分类;
          summary = a.diagnosis.一句话结论;
        } else if (a.status === 'running') {
          category = '进行中';
          summary = `打洞中（已耗时 ${a.startTime > 0 ? Date.now() - a.startTime : 0}ms）`;
        } else if (a.status === 'pending') {
          category = '待开始';
          summary = '尚未开始';
        } else {
          category = '未知';
          summary = a.failedReason || '尚未生成诊断';
        }

        // 失败时附 DataChannel 错误（如果有）到结论里
        if (diag?.dataChannelError && a.status === 'failed' && !summary.includes('DataChannel')) {
          summary += ` · DataChannel 错误: ${diag.dataChannelError.detail || diag.dataChannelError.message || 'unknown'}`;
        }

        const obj: Record<string, unknown> = {
          编号: a.index,
          轮次: a.round ?? null,
          状态: statusZh(a.status),
          STUN组合: a.stunServers.map(formatStun).join(' + '),
          耗时ms: a.endTime && a.startTime ? a.endTime - a.startTime : null,
          根因: category,
          结论: summary,
        };
        // 选中候选对（成功必有；失败时如果 stats 采到了也有）
        if (selectedPairStr) obj['选中候选对'] = selectedPairStr;
        if (localPublic && localPublic.length > 0) obj['本地公网'] = localPublic;
        if (remotePublic && remotePublic.length > 0) obj['远端公网'] = remotePublic;
        if (stunErrors) obj['STUN错误'] = stunErrors;

        // srflx-srflx 连通性证据（**关键诊断字段**）：
        // 用来区分"NAT 真打不通" vs "ICE 时间不够"
        //   - sent>0 recv=0 state=in-progress → 时间不够（延长超时可救）
        //   - sent>>20 recv=0                 → 真打不通（延长无效）
        //   - state=succeeded 但未 nominated  → 已通待提名（极接近成功）
        const srflxPair = diag?.finalStats?.allPairs.find(
          p => p.localType === 'srflx' && p.remoteType === 'srflx'
        );
        if (srflxPair) {
          obj['srflx连通性'] = {
            本地srflx: `${srflxPair.localAddress ?? '?'}:${srflxPair.localPort ?? '?'}`,
            远端srflx: `${srflxPair.remoteAddress ?? '?'}:${srflxPair.remotePort ?? '?'}`,
            状态: srflxPair.state,
            已被提名: srflxPair.nominated ?? false,
            发出请求数: srflxPair.requestsSent ?? 0,
            收到响应数: srflxPair.responsesReceived ?? 0,
            收到请求数: srflxPair.requestsReceived ?? 0,
            发出响应数: srflxPair.responsesSent ?? 0,
            当前RTT_ms: srflxPair.currentRoundTripTimeMs ?? null,
          };
        }

        // 诊断扩展（失败/中止 attempt 才输出，用来区分"srflx pair 没形成" vs "形成了但打不通"）
        if (a.status === 'failed' && diag) {
          const sync = diag.syncSnapshot;
          const ext: Record<string, unknown> = {
            候选对总数: diag.finalStats?.allPairs.length ?? 0,
            ICE状态终态: diag.iceStateTransitions.at(-1)?.state ?? null,
            候选收集终态: diag.gatheringStateTransitions.at(-1)?.state ?? null,
          };
          // 方案 M：fire 时刻 buffer 候选分布
          if (diag.fireBufferStats) {
            const s = diag.fireBufferStats;
            ext['fire时刻buffer'] = `host=${s.host} srflx=${s.srflx} relay=${s.relay} prflx=${s.prflx} unknown=${s.unknown} 共${s.total}`;
          }
          // 方案 M：fire 后到达的远端候选分布
          if (diag.postFireRemoteCandidates) {
            const s = diag.postFireRemoteCandidates;
            ext['fire后到达远端候选'] = `host=${s.host} srflx=${s.srflx} relay=${s.relay} prflx=${s.prflx} unknown=${s.unknown} 共${s.total}`;
          }
          // Sync 时序（只输出有数据的字段）
          if (sync) {
            // performance.now() 转 wall-clock ISO 时间（带毫秒精度）
            const fmtPerfNow = (perfMs: number): string => {
              const wallMs = performance.timeOrigin + perfMs;
              return new Date(wallMs).toISOString();
            };
            const syncOut: Record<string, unknown> = {};
            if (sync.rttMedianMs !== undefined) syncOut['RTT中位ms'] = sync.rttMedianMs;
            // 时钟偏差ms 仅作 syncDriftMs 计算的中间变量，绝对值反映两端时钟基准差异
            // （浏览器 perf.now 起算页面加载、Host steady_clock 起算系统启动），无业务意义，不输出
            if (sync.fireAtMs !== undefined) syncOut['fire_at'] = fmtPerfNow(sync.fireAtMs);
            if (sync.firedAtMs !== undefined) syncOut['fired_at'] = fmtPerfNow(sync.firedAtMs);
            if (sync.syncAccuracyMs !== undefined) syncOut['同步精度ms'] = sync.syncAccuracyMs;
            if (sync.syncDriftMs !== undefined) syncOut['双端fire偏差ms'] = sync.syncDriftMs;
            if (sync.holePunchMethod) syncOut['打洞方式'] = sync.holePunchMethod;
            if (sync.phase) syncOut['末态阶段'] = sync.phase;
            if (Object.keys(syncOut).length > 0) ext['Sync时序'] = syncOut;
          }
          obj['诊断扩展'] = ext;
        }

        // 成功时附吞吐/RTT（独立字段，便于 grep）
        if (a.status === 'success' && r) {
          obj['吞吐kbps'] = r.throughputKbps ?? null;
          if (typeof r.rttMs === 'number') obj['RTT_ms'] = Math.round(r.rttMs);
        }
        return obj;
      }),
    };
  }

  // 给 DataChannel 挂诊断 onerror
  private attachDcDiagnostic(dc: RTCDataChannel | null, attempt: StunAttempt): void {
    if (!dc) return;
    const diag = this.getDiag(attempt);
    const oldOnError = dc.onerror;
    dc.onerror = (ev: Event) => {
      const e = ev as RTCErrorEvent;
      const detail = (e.error as RTCError & { errorDetail?: string })?.errorDetail;
      diag.dataChannelError = {
        detail,
        message: e.error?.message || (e as unknown as { message?: string }).message,
        channelLabel: dc.label,
      };
      this.log('web', 'error', undefined, `DataChannel[${dc.label}] error: ${detail || e.error?.message || 'unknown'}`);
      if (typeof oldOnError === 'function') (oldOnError as (ev: Event) => void)(ev);
    };
  }


  // 启动 Sync 协议：自适应 fire_delay（方向 A，参考 libp2p DCUtR 用 RTT 动态计算）
  //
  // 历史方案 J：固定 fire_delay = 200ms。
  //   优点：避开 ctrl DC 走 TURN 时 ack 慢 2.5s 的根因。
  //   缺点：内地浪费（实际 RTT 14ms，过等 28 倍）；弱网（RTT >500ms）双端 fire 严重不同步。
  //
  // 方向 A：fire_delay = max(150, turnAvgRtt + 50)
  //   - 复用已有 getTurnAvgRtt（持续记录的主 TURN 连接 RTT 历史）
  //   - 不依赖 ctrl DC 测 RTT（避免方案 A 失败的根因）
  //   - 自适应网络：内地 ~150ms 保底，跨国 ~250ms，弱网 ~650ms+
  //   - 公式参考 libp2p DCUtR 规范的 RTT/2 + safety margin 思路
  private async runSyncProtocol(session: AssistSession): Promise<void> {
    // RTT_MEASURE 步骤：标 skipped（不再实测，复用已有 turnAvgRtt）
    this.updateStep(session.attempt, Steps.RTT_MEASURE, 'skipped', undefined, '已跳过：复用主连接 RTT');
    session.syncInfo.phase = 'sync';
    session.syncInfo.holePunchMethod = 'sync';
    this.emitStateChange();

    // 自适应 fire_delay：基于已建立的主连接（TURN）RTT 历史
    // 没数据时（首次连接 RTT 还没积累）fallback 200ms 保守值
    const turnAvgRtt = this.getTurnAvgRtt();
    const fireDelayMs = turnAvgRtt !== null
      ? Math.max(150, Math.round(turnAvgRtt + 50))
      : 200;
    const rttForHost = turnAvgRtt !== null ? Math.round(turnAvgRtt) : 30;

    const fireAtMs = performance.now() + fireDelayMs;
    session.syncInfo.fireAtMs = fireAtMs;
    session.syncInfo.rttMedianMs = rttForHost;
    this.updateStep(session.attempt, Steps.SYNC_NEGOTIATE, 'running',
      undefined, `fire_delay=${fireDelayMs}ms (turnRtt=${turnAvgRtt !== null ? turnAvgRtt.toFixed(0) + 'ms' : 'n/a'})`);

    // 立即通知 Host：rtt_median_ms 用真实主连接 RTT，Host 端用其计算 RTT/2 补偿
    // 诊断：记下发送时刻，配合 sync_ack 算时钟偏差
    session.syncNegotiateSentAtMs = performance.now();
    this.sendCtrl({
      type: 'p2p_assist_ctrl',
      action: 'sync_negotiate',
      sessionId: session.id,
      fire_delay_ms: fireDelayMs,
      rtt_median_ms: rttForHost,
    });

    // 调度浏览器端 fire：到 fire_at 时 flush buffered 候选
    session.syncFireTimer = window.setTimeout(() => this.fireSyncFlush(session), fireDelayMs);
    this.emitStateChange();
  }

  // fire_at 时刻：关闭 sync 模式，把所有 buffered Host 候选注入 ICE agent
  private fireSyncFlush(session: AssistSession): void {
    if (!session.syncMode) return;
    session.syncMode = false;
    const firedAt = performance.now();
    session.syncInfo.firedAtMs = firedAt;
    if (session.syncInfo.fireAtMs != null) {
      session.syncInfo.syncAccuracyMs = Math.round(firedAt - session.syncInfo.fireAtMs);
    }
    session.syncInfo.phase = 'fired';
    if (!session.syncInfo.holePunchMethod) session.syncInfo.holePunchMethod = 'sync';

    // 方案 M：fire 时刻 buffer 内候选按 type 分布（验证假设：失败 attempt 是否 buffer 缺 srflx）
    const stats: CandidateTypeCounts = { host: 0, srflx: 0, prflx: 0, relay: 0, unknown: 0, total: 0 };
    for (const cand of session.bufferedHostCandidates) {
      const ci = this.parseCandidate(cand);
      const t = ci?.type ?? 'unknown';
      if (t === 'host') stats.host++;
      else if (t === 'srflx') stats.srflx++;
      else if (t === 'prflx') stats.prflx++;
      else if (t === 'relay') stats.relay++;
      else stats.unknown++;
      stats.total++;
    }
    this.getDiag(session.attempt).fireBufferStats = stats;

    this.log('web', 'success', Steps.SYNC_FIRE,
      `🔥 FIRE! 注入 ${stats.total} 个 buffered Host 候选 [host=${stats.host} srflx=${stats.srflx} relay=${stats.relay}] (误差=${session.syncInfo.syncAccuracyMs ?? 'n/a'}ms)`);
    this.updateStep(session.attempt, Steps.SYNC_FIRE, 'success', undefined,
      `注入 ${stats.total} 个候选 (host=${stats.host} srflx=${stats.srflx})`);

    if (session.pc) {
      for (const cand of session.bufferedHostCandidates) {
        session.pc.addIceCandidate(new RTCIceCandidate(cand)).catch((err) => {
          this.log('web', 'error', Steps.SYNC_FIRE, `注入候选失败: ${err.message}`);
        });
      }
    }
    session.bufferedHostCandidates = [];
    this.emitStateChange();
  }

  // 处理 Host 发来的 winning candidate 信息（覆盖浏览器侧无法推断的 remote STUN url）
  private handleHostCandidateInfo(data: {
    sessionId: string;
    candidateType?: string;
    url?: string;
    address?: string;
    port?: number;
    candidates?: Array<{ type?: string; url?: string; address?: string; port?: number; protocol?: string; relatedAddress?: string; relatedPort?: number }>;
  }): void {
    if (!data?.sessionId) return;
    // 打洞报告：把 host 上报的候选信息（无论 winning 单条还是 candidates 数组）补全到 attempt.diagnostics.remoteCandidates
    // 用 sessionId 找 attempt（无论 attempt 当前是否在 establishedConnections）
    const session = this.sessions.get(data.sessionId);
    const attempt = session?.attempt
      || this.attemptHistory.find(a => a.sessionId === data.sessionId)
      || null;
    if (attempt) {
      const diag = this.getDiag(attempt);
      const tryUpgradeUrl = (info: { type?: string; url?: string; address?: string; port?: number; protocol?: string; relatedAddress?: string; relatedPort?: number }): void => {
        if (!info.address || !info.port) return;
        // 找到已有同 address+port 的远端候选，回填 url；找不到就 push 一条
        const existing = diag.remoteCandidates.find(c => c.address === info.address && c.port === info.port);
        const t = info.type as CandidateInfo['type'];
        const validType: CandidateInfo['type'] =
          t === 'host' || t === 'srflx' || t === 'prflx' || t === 'relay' ? t : 'unknown';
        if (existing) {
          if (info.url && !existing.url) existing.url = info.url;
          if (validType !== 'unknown' && existing.type === 'unknown') existing.type = validType;
          if (info.relatedAddress && !existing.relatedAddress) existing.relatedAddress = info.relatedAddress;
          if (info.relatedPort && !existing.relatedPort) existing.relatedPort = info.relatedPort;
        } else {
          diag.remoteCandidates.push({
            type: validType,
            protocol: info.protocol === 'tcp' ? 'tcp' : info.protocol === 'udp' ? 'udp' : 'unknown',
            address: info.address,
            port: info.port,
            relatedAddress: info.relatedAddress,
            relatedPort: info.relatedPort,
            url: info.url || null,
          });
        }
      };
      // 兼容：旧字段（winning 单条）
      if (data.address && data.port) {
        tryUpgradeUrl({ type: data.candidateType, url: data.url, address: data.address, port: data.port });
      }
      // 新字段：完整候选数组（Host 后续扩展时使用）
      if (Array.isArray(data.candidates)) {
        for (const c of data.candidates) tryUpgradeUrl(c);
      }
    }

    // 现有 UI 行为：填 established.remoteCandidate（仅 winning 单条）
    const conn = this.establishedConnections.get(data.sessionId);
    if (!conn) {
      this.log('web', 'info', undefined,
        `收到 host_candidate_info 但 session 尚未 established: ${data.sessionId}`);
      return;
    }
    const stunLabel = data.url ? `STUN(${data.url.replace(/^stun:/, '')})` : (data.candidateType || 'remote');
    conn.remoteCandidate = `${stunLabel} → ${data.address}:${data.port} [${data.candidateType || '?'}]`;
    this.log('web', 'success', undefined,
      `Host 端 winning candidate: ${conn.remoteCandidate}`);
    this.emitStateChange();
  }

  // 处理来自 Host 的 p2p_assist_ctrl 消息（rtt_probe_ack / sync_ack）
  private handleCtrlMessage(data: Record<string, unknown>): void {
    const action = data.action as string;
    const sessionId = data.sessionId as string;
    const session = sessionId ? this.sessions.get(sessionId) : null;
    if (!session) return;

    if (action === 'rtt_probe_ack') {
      const seq = Number(data.seq);
      const probe = session.rttProbes.get(seq);
      if (probe) {
        probe.tRecvMs = performance.now();
        probe.tHostRecvMs = Number(data.t_host_recv_ms);
        probe.tHostSendMs = Number(data.t_host_send_ms);
      }
      return;
    }
    if (action === 'sync_ack') {
      session.syncAckReceived = true;
      // 诊断：用 sync_ack 的往返算"sync_negotiate 单程时延"，进而算双端时钟偏差
      // clock_offset (host - browser) = t_host_recv - (t_send + 单程)
      const tHostRecv = Number(data.t_host_recv_ms);
      const tSend = session.syncNegotiateSentAtMs;
      if (tSend !== undefined && Number.isFinite(tHostRecv) && tHostRecv > 0) {
        const recvAt = performance.now();
        const oneWay = (recvAt - tSend) / 2;
        session.syncInfo.clockOffsetMs = Math.round(tHostRecv - (tSend + oneWay));
      }
      // Host 端 fire_delay_ms（仅诊断，浏览器端仍按自己 setTimeout）
      this.updateStep(session.attempt, Steps.SYNC_NEGOTIATE, 'success', undefined, 'host acked');
      this.emitStateChange();
      return;
    }
    if (action === 'sync_fire_done') {
      // 诊断：Host 实际 fire 时刻上报，配合时钟偏差算双端 fire 偏差
      // 浏览器 fire 在 Host 时钟里的时刻 = firedAtMs + clockOffset
      // 双端 fire 偏差 = t_host_fire - 浏览器 fire 在 Host 时钟里的时刻
      const tHostFire = Number(data.t_host_fire_ms);
      const fired = session.syncInfo.firedAtMs;
      const offset = session.syncInfo.clockOffsetMs;
      if (Number.isFinite(tHostFire) && fired !== undefined && offset !== undefined) {
        const browserFireInHostClock = fired + offset;
        session.syncInfo.syncDriftMs = Math.round(tHostFire - browserFireInHostClock);
        this.log('web', 'info', Steps.SYNC_FIRE,
          `双端 fire 偏差: ${session.syncInfo.syncDriftMs}ms (Host 比浏览器 ${session.syncInfo.syncDriftMs > 0 ? '晚' : '早'} fire)`);
        this.emitStateChange();
      }
      return;
    }
    if (action === 'host_stats') {
      // Host 视角的实时候选对统计（每 500ms 上报一次）
      // 写入 attempt.diagnostics.hostFinalStats，让 panel 显示双端对称的"候选对详情"
      this.getDiag(session.attempt).hostFinalStats = {
        state: data.state as string,
        nominated: !!data.nominated,
        localType: (data.localType as string) || '',
        localAddress: (data.localAddress as string) || '',
        localPort: Number(data.localPort) || 0,
        localUrl: (data.localUrl as string) || undefined,
        remoteType: (data.remoteType as string) || '',
        remoteAddress: (data.remoteAddress as string) || '',
        remotePort: Number(data.remotePort) || 0,
        requestsSent: Number(data.requestsSent) || 0,
        requestsReceived: Number(data.requestsReceived) || 0,
        responsesSent: Number(data.responsesSent) || 0,
        responsesReceived: Number(data.responsesReceived) || 0,
      };
      this.emitStateChange();
      return;
    }
  }

  // 主连接进入"非建立态"（断开/失败/重连）时重置 P2P Assist 状态并停止旧 Assist。
  // 等 TURN 重连成功后 stats:update 会自动触发 checkMainConnectionType 启动新 Assist。
  // 注意：data.state 来自 WebRTCService 的业务层 ConnectionState 枚举（types.ts），
  //       不是 RTCPeerConnection.iceConnectionState。'closed' / 'new' 不在枚举里，已删除。
  private handleConnectionStateChange(data: { state: string; previousState: string }): void {
    const s = data.state;
    if (s === 'disconnected' || s === 'failed' || s === 'reconnecting') {
      if (this.consumed) {
        this.log('web', 'info', undefined, `连接状态=${s}，重置P2P Assist consumed 标志`);
      }
      this.consumed = false;
      this.mainConnectionType = 'unknown';
      this.pendingSwitch = null;
      this.clearForceSwitchCountdown();
      // 主动停止旧 Assist：否则 isRunning=true 会让 checkMainConnectionType 的
      // start 分支永远进不去，导致重连后不再打洞
      if (this.isRunning) {
        this.log('web', 'info', undefined, `主连接 ${s}，主动停止旧 P2P Assist`);
        this.stop();
      }
    }
  }

  // 处理 icePolicy 变更
  private handleIcePolicyChange(icePolicy: string): void {
    this.forceRelayMode = icePolicy === 'relay';
    if (this.forceRelayMode && this.isRunning) {
      this.log('web', 'warning', undefined, '强制中转模式，停止辅助打洞');
      this.stop();
    }
  }

  // 设置强制中转模式
  setForceRelayMode(forceRelay: boolean): void {
    this.forceRelayMode = forceRelay;
    if (forceRelay && this.isRunning) {
      this.log('web', 'warning', undefined, '强制中转模式，停止辅助打洞');
      this.stop();
    }
  }

  // 检查主连接类型
  private checkMainConnectionType(data: { webrtc?: { transportType?: string; rtt?: number; packetLoss?: number } }): void {
    const type = data?.webrtc?.transportType;

    // 持续记录当前主连接（turn）的 RTT / loss 历史，用于 P2P 质量对比
    // 只在主连接是 relay 时记录（P2P 后就不需要对比了）
    if (type === 'relay') {
      const rtt = data?.webrtc?.rtt;
      const loss = data?.webrtc?.packetLoss;
      if (typeof rtt === 'number' && rtt > 0) {
        this.turnRttHistory.push(rtt);
        if (this.turnRttHistory.length > P2PAssistService.TURN_STATS_HISTORY_SIZE) {
          this.turnRttHistory.shift();
        }
      }
      if (typeof loss === 'number' && loss >= 0) {
        this.turnLossHistory.push(loss);
        if (this.turnLossHistory.length > P2PAssistService.TURN_STATS_HISTORY_SIZE) {
          this.turnLossHistory.shift();
        }
      }
    }

    if (type === 'relay' && this.mainConnectionType !== 'relay') {
      this.mainConnectionType = 'relay';
      this.log('web', 'info', undefined, '检测到主连接为Relay模式');
      // 自动启动辅助打洞（仅在非强制中转模式且本次连接尚未消费时）
      if (this.consumed) {
        this.log('web', 'info', undefined, '本次连接 P2P Assist 已执行过，跳过');
      } else if (!this.isRunning && !this.forceRelayMode) {
        this.log('web', 'info', undefined, '自动启动P2P辅助打洞');
        this.start();
      } else if (this.forceRelayMode) {
        this.log('web', 'info', undefined, '强制中转模式，跳过辅助打洞');
      }
    } else if (type === 'p2p' && this.mainConnectionType !== 'p2p') {
      this.mainConnectionType = 'p2p';
      this.log('web', 'success', undefined, '主连接已是P2P模式');
      // 本次连接已直接是 P2P（ICE Racing 或已切换成功），无需再启动 Assist
      this.consumed = true;
      this.clearForceSwitchCountdown();
    }
  }

  // 获取当前 turn 连接的 RTT 平均值（最近 5 个样本），无数据返回 null
  private getTurnAvgRtt(): number | null {
    if (this.turnRttHistory.length === 0) return null;
    const sum = this.turnRttHistory.reduce((a, b) => a + b, 0);
    return sum / this.turnRttHistory.length;
  }

  // 获取当前 turn 连接的丢包率平均值（最近 5 个样本），无数据返回 null
  private getTurnAvgLoss(): number | null {
    if (this.turnLossHistory.length === 0) return null;
    const sum = this.turnLossHistory.reduce((a, b) => a + b, 0);
    return sum / this.turnLossHistory.length;
  }

  /**
   * 判断 P2P 质量是否"更优于"当前 turn 连接
   * 规则（任一满足即为更优）：
   *   - p2p_rtt <= turn_rtt
   *   - p2p_loss <= turn_loss
   *
   * 注意：turn 历史样本不足时（< 1 个），无法对比，返回 true（假设更优，保持原有切换行为）
   * 这是保守策略：宁可误切换也不漏切换
   */
  private isP2PMoreOptimal(p2pRttMs: number, p2pLossRatePercent: number): { better: boolean; reason: string } {
    const turnAvgRtt = this.getTurnAvgRtt();
    const turnAvgLoss = this.getTurnAvgLoss();

    if (turnAvgRtt === null && turnAvgLoss === null) {
      return { better: true, reason: 'turn 质量数据不足，默认切换' };
    }

    const rttBetter = turnAvgRtt !== null && p2pRttMs <= turnAvgRtt;
    const lossBetter = turnAvgLoss !== null && p2pLossRatePercent <= turnAvgLoss;

    if (rttBetter) {
      return { better: true, reason: `P2P RTT ${p2pRttMs.toFixed(0)}ms <= turn ${turnAvgRtt!.toFixed(0)}ms` };
    }
    if (lossBetter) {
      return { better: true, reason: `P2P loss ${p2pLossRatePercent.toFixed(2)}% <= turn ${turnAvgLoss!.toFixed(2)}%` };
    }

    const rttStr = turnAvgRtt !== null ? `${p2pRttMs.toFixed(0)}ms>${turnAvgRtt.toFixed(0)}ms` : 'turn_rtt=NA';
    const lossStr = turnAvgLoss !== null ? `${p2pLossRatePercent.toFixed(2)}%>${turnAvgLoss.toFixed(2)}%` : 'turn_loss=NA';
    return { better: false, reason: `P2P 不更优: rtt ${rttStr}, loss ${lossStr}` };
  }

  // 启动辅助打洞
  start(): void {
    if (this.consumed) {
      this.log('web', 'info', undefined, '本次连接 P2P Assist 已执行过，跳过启动');
      return;
    }
    if (this.forceRelayMode) {
      this.log('web', 'warning', undefined, '强制中转模式，禁止启动辅助打洞');
      return;
    }
    if (this.isRunning) {
      this.log('web', 'warning', undefined, '辅助打洞已在运行中');
      return;
    }

    this.isRunning = true;
    this.roundCount = 0;
    this.bestAttempt = null;
    this.logs = [];
    // 打洞报告：start = 全新打洞，跨轮累积报告也清零
    this.attemptHistory = [];
    this.attempts = [];
    this.currentAttemptIndex = 0;
    this.firstEstablishedAt = null;
    // 新架构辅助字段清理
    this.pendingPrepareSessionId = null;
    if (this.prepareTimeoutTimer) { clearTimeout(this.prepareTimeoutTimer); this.prepareTimeoutTimer = null; }
    if (this.notifyToastTimer) { clearTimeout(this.notifyToastTimer); this.notifyToastTimer = null; }
    this.notifyPendingCount = 0;
    this.clearForceSwitchCountdown();

    this.log('web', 'info', undefined, '启动P2P辅助打洞服务');
    this.emitStateChange();

    // 开始第一轮
    this.startNewRound();
  }

  // 开始新一轮STUN遍历
  private startNewRound(): void {
    this.roundCount++;
    this.currentAttemptIndex = 0;
    this.attempts = [];

    this.log('web', 'info', undefined, `开始第 ${this.roundCount}/${this.MAX_ROUNDS} 轮STUN遍历`);

    // 初始化尝试记录（每次使用1个STUN服务器，与 Flutter 桌面端对齐 — 共 12 STUN → 单轮 12 个 attempt）
    const STUN_PER_ATTEMPT = 1;
    for (let i = 0; i < ASSIST_STUN_SERVERS.length; i += STUN_PER_ATTEMPT) {
      const servers = ASSIST_STUN_SERVERS.slice(i, i + STUN_PER_ATTEMPT);
      this.attempts.push(this.createAttempt(this.attempts.length, servers));
    }

    // 开始尝试第一组STUN服务器
    this.tryNextStunServer();
  }

  /**
   * 停止辅助打洞
   *
   * 所有占用的 P2P Assist 资源（PC / DataChannel / stream / timer /
   * bestAttempt / pendingSwitch / TURN 历史）都会被彻底清理，回到未启动初态。
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.log('web', 'info', undefined, '停止P2P辅助打洞服务');

    // 新架构辅助字段清理
    this.pendingPrepareSessionId = null;
    if (this.prepareTimeoutTimer) { clearTimeout(this.prepareTimeoutTimer); this.prepareTimeoutTimer = null; }
    if (this.notifyToastTimer) { clearTimeout(this.notifyToastTimer); this.notifyToastTimer = null; }
    this.notifyPendingCount = 0;
    this.clearForceSwitchCountdown();

    // 统一清理所有资源
    this.finalizeResources(FinalizeMode.DiscardAll);

    // 发送停止消息到GO端
    signalingService.send({
      type: 'p2p_assist',
      action: 'stop',
    });
  }

  // 创建尝试记录
  private createAttempt(index: number, stunServers: string[]): StunAttempt {
    const steps: StepStatus[] = [];
    // 含 Sync 扩展步骤 17~20，与原 1~16 合并按顺序呈现
    for (let i = 1; i <= 20; i++) {
      steps.push({
        step: i,
        name: StepNames[i] || `步骤${i}`,
        side: StepSides[i] || 'both',
        status: 'pending',
      });
    }

    const attempt: StunAttempt = {
      index,
      stunServers,
      sessionId: `assist-${Date.now()}-${index}`,
      status: 'pending',
      steps,
      startTime: 0,
      webCandidates: [],
      goCandidates: [],
      round: this.roundCount,
      diagnostics: {
        signalingTimings: {},
        localCandidates: [],
        remoteCandidates: [],
        candidateErrors: [],
        iceStateTransitions: [],
        gatheringStateTransitions: [],
      },
    };
    // 跨轮累积：同一引用，attempt 后续被 update 时报告自动反映最新数据
    this.attemptHistory.push(attempt);
    return attempt;
  }

  // 尝试下一组STUN服务器
  private tryNextStunServer(): void {
    if (!this.isRunning) return;

    if (this.currentAttemptIndex >= this.attempts.length) {
      // 所有STUN服务器都尝试完了
      this.log('web', 'info', undefined, '所有STUN服务器尝试完成');
      this.onAllAttemptsComplete();
      return;
    }

    const attempt = this.attempts[this.currentAttemptIndex];
    attempt.status = 'running';
    attempt.startTime = Date.now();
    attempt.sessionId = `assist-${Date.now()}-${this.currentAttemptIndex}`;

    const serversStr = attempt.stunServers.join(', ');
    this.log('web', 'info', undefined, `开始尝试 #${attempt.index + 1}: ${serversStr}`);
    this.emitStateChange();

    // 创建辅助会话
    this.createAssistSession(attempt);
  }

  // 创建辅助会话
  private async createAssistSession(attempt: StunAttempt): Promise<void> {
    const sessionId = attempt.sessionId;

    // 步骤1: 创建PeerConnection
    this.updateStep(attempt, Steps.WEB_CREATE_PC, 'running');

    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: attempt.stunServers }],
      });

      const serversStr = attempt.stunServers.join(', ');
      this.updateStep(attempt, Steps.WEB_CREATE_PC, 'success', undefined, `使用STUN: ${serversStr}`);

      // 打洞报告：PC 创建后立即挂诊断 hook（onicecandidateerror / onicegatheringstatechange / onconnectionstatechange）
      this.attachDiagnosticHooks(pc, attempt);

      // 步骤2: 创建DataChannel
      this.updateStep(attempt, Steps.WEB_CREATE_DC, 'running');
      const dc = pc.createDataChannel('p2p-assist', { ordered: true });
      this.attachDcDiagnostic(dc, attempt);

      // 创建quality-test DataChannel（不可靠，用于丢包检测）
      const qualityChannel = pc.createDataChannel('quality-test', {
        ordered: false,
        maxRetransmits: 0
      });
      this.attachDcDiagnostic(qualityChannel, attempt);

      // input: 必须由浏览器创建，Host OnDataChannel 会用它替换 data_channel_，
      //        确保双端用同一个 SCTP stream 通信
      // mouse: 不创建，使用 Host 在 AddTracks 中创建的（已注册 observer）
      // file-ctrl/file-data: 懒加载，由 Host ondatachannel 提供或按需创建
      const inputChannel = pc.createDataChannel('input', { ordered: true, priority: 'high' });
      this.attachDcDiagnostic(inputChannel, attempt);

      this.updateStep(attempt, Steps.WEB_CREATE_DC, 'success');

      // 添加video transceiver用于接收视频（recvonly）
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      // 创建会话对象
      const session: AssistSession = {
        id: sessionId,
        stunServers: attempt.stunServers,
        pc,
        dataChannel: dc,
        qualityChannel,
        inputChannel,            // 浏览器创建的 'input'，Host 用它替换 data_channel_
        mouseChannel: null,      // Host 的 'mouse' 通道（ondatachannel 接收）
        fileCtrlChannel: null,   // 懒加载
        fileDataChannel: null,   // 懒加载
        attempt,
        qualityTest: null,
        connectTimeout: null,
        qualityTestTimeout: null,
        answerReceived: false,
        remoteStream: null,
        // ----- Sync 打洞初始状态 -----
        syncMode: true,                      // 默认启用；若 Host 不支持则 3s watchdog 立即 flush
        bufferedHostCandidates: [],
        syncFireTimer: null,
        syncInfo: { phase: 'rtt' },
        rttProbes: new Map(),
        syncAckReceived: false,
        qualityTestCompleted: false,
        statsPollTimer: null,
      };
      this.sessions.set(sessionId, session);

      // 接收 Host 创建的通道
      pc.ondatachannel = (event) => {
        // SCTP 已通的最强信号 —— host 能 createDataChannel 并通知到浏览器，链路必然活着（方案 C 保险）
        // 浏览器自建的 dc/qualityChannel 在双端各自 createDataChannel 时也可能永不 open，
        // 多挂一道入口避免依赖单一 onopen 事件。
        this.clearConnectTimeout(session, 'pc.ondatachannel');
        const channel = event.channel;
        this.attachDcDiagnostic(channel, attempt);
        if (channel.label === 'data') {
          // 不覆盖 inputChannel（浏览器创建的 'input'），Host 的 'data' 是重复通道
          // Host 的 data_channel_ 指向浏览器的 'input'，performSwitch 必须传同一个
          this.log('web', 'info', undefined, `收到 Host data 通道（忽略，使用浏览器创建的 input）`);
        } else if (channel.label === 'mouse') {
          session.mouseChannel = channel;
          this.log('web', 'info', undefined, `收到 Host mouse 通道`);
        } else if (channel.label === 'quality-test') {
          // 使用 Host 创建的 quality-test 通道替代浏览器创建的
          channel.onmessage = (ev) => {
            this.handleQualityPacket(session, ev.data);
          };
          this.log('web', 'info', undefined, `收到 Host quality-test 通道`);
        }
      };

      // 设置quality-test通道消息处理（浏览器自建的 quality-test，作为备选）
      qualityChannel.onmessage = (event) => {
        this.handleQualityPacket(session, event.data);
      };
      // qualityChannel 一旦 open 说明 SCTP 已通，这是"链路活着"的最强信号 —— 立即清除连接超时
      qualityChannel.onopen = () => {
        this.clearConnectTimeout(session, 'qc.onopen');
      };

      // 设置ontrack回调接收视频流
      pc.ontrack = (event) => {
        this.log('web', 'success', undefined, `收到${event.track.kind}轨道`);
        if (!session.remoteStream) {
          session.remoteStream = new MediaStream();
        }
        session.remoteStream.addTrack(event.track);
      };

      // 设置ICE候选回调
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const candStr = `${event.candidate.address}:${event.candidate.port} (${event.candidate.type})`;
          attempt.webCandidates.push(candStr);
          this.log('web', 'info', Steps.ICE_GATHERING, `ICE候选: ${candStr}`);
          this.updateStep(attempt, Steps.ICE_GATHERING, 'running', undefined,
            `已收集${attempt.webCandidates.length}个候选`);

          // 打洞报告：解析候选保存到 diagnostics（含 STUN url）
          const ci = this.parseCandidate(event.candidate);
          if (ci) this.getDiag(attempt).localCandidates.push(ci);

          // 方案 T：本地 host 候选不发给 Host 端
          // FF 私网 IP 过滤已回滚（实测 192.168.x.x 的 srflx 在双层 NAT 场景下实际有用）
          // Host 端不知道我们的 host 候选 → 不形成 host:* pair → ICE 减少 host 路径竞争
          if (ci?.type === 'host') {
            this.log('web', 'info', Steps.ICE_GATHERING,
              `[过滤] 本地 host 候选 ${ci.address}:${ci.port}（不发给 Host）`);
          } else {
            // 发送 srflx/relay 候选到 GO 端（本地非 host 候选立即送出，Host 端 sync 模式会 buffer）
            signalingService.send({
              type: 'p2p_assist',
              action: 'candidate',
              sessionId,
              candidate: event.candidate.toJSON(),
            });
          }
        } else {
          // ICE收集完成
          this.log('web', 'info', Steps.ICE_GATHERING, `ICE收集完成，共${attempt.webCandidates.length}个候选`);
          this.updateStep(attempt, Steps.ICE_GATHERING, 'success', undefined,
            `收集完成，共${attempt.webCandidates.length}个候选`);
        }
        this.emitStateChange();
      };

      // 设置ICE连接状态回调
      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        // 打洞报告：每次状态变化都记录 timeline
        this.getDiag(attempt).iceStateTransitions.push({ atMs: this.msFromStart(attempt), state });
        this.log('web', 'info', Steps.ICE_CONNECTING, `ICE状态: ${state}`);
        this.updateStep(attempt, Steps.ICE_CONNECTING, 'running', undefined, `状态: ${state}`);

        if (state === 'connected' || state === 'completed') {
          this.clearConnectTimeout(session, `iceState=${state}`);
          this.updateStep(attempt, Steps.ICE_CONNECTING, 'success', undefined, '连接已建立');
        } else if (state === 'failed') {
          this.handleSessionFailed(session, Steps.ICE_CONNECTING, 'ICE连接失败');
        }
        this.emitStateChange();
      };

      // 方案 C：connectionState 综合 ICE+DTLS+SCTP，比 iceConnectionState 更权威，
      // 在 Chromium 实现里通常领先 iceConnectionState 几十 ms 切到 'connected'。
      // 覆盖 attachDiagnosticHooks 里的赋值，但保留诊断写入。
      pc.onconnectionstatechange = () => {
        this.getDiag(attempt).peerConnectionState = pc.connectionState;
        if (pc.connectionState === 'connected') {
          this.clearConnectTimeout(session, 'pcState=connected');
        }
      };

      // 设置DataChannel回调
      dc.onopen = () => {
        this.clearConnectTimeout(session, 'dc.onopen');
        this.log('web', 'success', undefined, 'DataChannel已打开');
      };

      dc.onmessage = (event) => {
        // 可靠通道用于控制消息，质量测试通过qualityChannel处理
        this.log('web', 'info', undefined, `收到控制消息: ${event.data.length || event.data.byteLength} bytes`);
      };

      // 步骤3: 创建Offer
      this.updateStep(attempt, Steps.WEB_CREATE_OFFER, 'running');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.getDiag(attempt).signalingTimings.offerCreatedAt = this.msFromStart(attempt);
      this.updateStep(attempt, Steps.WEB_CREATE_OFFER, 'success');

      // 步骤4: 发送Offer到GO
      this.updateStep(attempt, Steps.WEB_SEND_OFFER, 'running');
      this.log('web', 'info', Steps.WEB_SEND_OFFER, '发送Start+Offer到GO');

      signalingService.send({
        type: 'p2p_assist',
        action: 'start',
        sessionId,
        stunServers: attempt.stunServers,
        sdp: offer.sdp,
      });
      this.getDiag(attempt).signalingTimings.offerSentAt = this.msFromStart(attempt);

      this.updateStep(attempt, Steps.WEB_SEND_OFFER, 'success');

      // 设置连接超时
      session.connectTimeout = window.setTimeout(() => {
        if (!session.answerReceived) {
          this.handleSessionFailed(session, Steps.WEB_SEND_OFFER, 'GO端无响应超时');
        } else if (pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
          this.handleSessionFailed(session, Steps.ICE_CONNECTING, `ICE连接超时 (状态: ${pc.iceConnectionState})`);
        }
      }, this.CONNECT_TIMEOUT);

      // 启动实时 stats 轮询：每 500ms 把 pc.getStats 提取的 selectedPair / sent / recv
      // 写入 diagnostics.finalStats，让 panel 在 attempt 跑中途也能实时显示候选对详情。
      // attempt 终结时由 cleanupSession 清掉这个定时器。
      session.statsPollTimer = window.setInterval(() => {
        if (!session.pc) return;
        void this.collectFinalStats(session);
        this.emitStateChange();
      }, 500);

      this.emitStateChange();
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      this.handleSessionFailed({ id: sessionId, attempt } as AssistSession, Steps.WEB_CREATE_PC, errMsg);
    }
  }

  // 处理Answer
  private handleAnswer(data: { sessionId: string; sdp: string }): void {
    const session = this.sessions.get(data.sessionId);
    if (!session || !session.pc) {
      this.log('web', 'warning', undefined, `收到未知会话的Answer: ${data.sessionId}`);
      return;
    }

    session.answerReceived = true;
    this.getDiag(session.attempt).signalingTimings.answerReceivedAt = this.msFromStart(session.attempt);
    this.log('web', 'info', Steps.WEB_SET_REMOTE_DESC, '收到Answer');
    this.updateStep(session.attempt, Steps.WEB_SET_REMOTE_DESC, 'running');

    session.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp })
      .then(() => {
        this.getDiag(session.attempt).signalingTimings.setRemoteAt = this.msFromStart(session.attempt);
        this.updateStep(session.attempt, Steps.WEB_SET_REMOTE_DESC, 'success');
        this.emitStateChange();
        // SDP 交换完成，开启 Sync 协议（RTT ping-pong + sync_negotiate + fire 调度）
        // 与现有 ICE gathering 并行执行；fire 到达前 Host 候选已被 buffer
        this.runSyncProtocol(session).catch((err) => {
          this.log('web', 'error', Steps.SYNC_NEGOTIATE, `Sync 协议异常: ${err}`);
          // 异常情况下立即 flush，走 fallback（无 sync）
          session.syncInfo.holePunchMethod = 'fallback_immediate';
          this.fireSyncFlush(session);
        });
      })
      .catch((err) => {
        this.handleSessionFailed(session, Steps.WEB_SET_REMOTE_DESC, err.message);
      });
  }

  // 处理ICE候选
  private handleCandidate(data: { sessionId: string; candidate: RTCIceCandidateInit }): void {
    const session = this.sessions.get(data.sessionId);
    if (!session || !session.pc) return;

    // 方案 T：远端 host 候选直接丢弃（host pair 跨 NAT 永远不通）
    // FF 私网 IP 过滤已回滚（实测 192.168.x.x 的 srflx 在双层 NAT 场景下实际有用）
    // 仅记录到 diagnostics 用作报告，不进 ICE agent
    let cand: RTCIceCandidateInit = data.candidate;
    const rawStr = cand.candidate || '';
    if (rawStr) {
      const parts = rawStr.split(/\s+/);
      if (parts.length >= 8 && parts[7] === 'host') {
        const ci = this.parseCandidate(cand);
        if (ci) this.getDiag(session.attempt).remoteCandidates.push(ci);
        this.log('go', 'info', Steps.ICE_GATHERING,
          `[过滤] 远端 host 候选 ${parts[4]}:${parts[5]}（不进 ICE）`);
        return;
      }
    }

    // 记录GO端候选（仅 srflx/relay 等非 host 类型走到这里）
    const candStr = cand.candidate || '';
    if (candStr) {
      session.attempt.goCandidates.push(candStr);
      this.log('go', 'info', Steps.ICE_GATHERING, `ICE候选: ${candStr.substring(0, 50)}...`);
      // 打洞报告：解析远端候选保存到 diagnostics（远端 url 拿不到，留空 / 由 host_candidate_info 后补）
      const ci = this.parseCandidate(cand as RTCIceCandidateInit);
      if (ci) this.getDiag(session.attempt).remoteCandidates.push(ci);
    }

    // Sync 打洞：fire_at 之前 buffer 所有 Host 候选，不立即 addIceCandidate
    // fire 后到达的迟到候选走正常路径。
    if (session.syncMode) {
      session.bufferedHostCandidates.push(cand);
      this.log('web', 'info', Steps.ICE_GATHERING,
        `[Sync] 缓冲 Host 候选 (已缓冲 ${session.bufferedHostCandidates.length} 个)`);
    } else {
      // 方案 M：fire 后到达的远端候选累加按 type 计数（验证"远端 srflx 在 fire 后才到"假设）
      const diag = this.getDiag(session.attempt);
      if (!diag.postFireRemoteCandidates) {
        diag.postFireRemoteCandidates = { host: 0, srflx: 0, prflx: 0, relay: 0, unknown: 0, total: 0 };
      }
      const ciPost = this.parseCandidate(cand as RTCIceCandidateInit);
      const t = ciPost?.type ?? 'unknown';
      const c = diag.postFireRemoteCandidates;
      if (t === 'host') c.host++;
      else if (t === 'srflx') c.srflx++;
      else if (t === 'prflx') c.prflx++;
      else if (t === 'relay') c.relay++;
      else c.unknown++;
      c.total++;

      session.pc.addIceCandidate(new RTCIceCandidate(cand))
        .catch((err) => {
          this.log('web', 'error', Steps.ICE_GATHERING, `添加ICE候选失败: ${err.message}`);
        });
    }

    this.emitStateChange();
  }

  // 处理GO端步骤更新
  private handleStepUpdate(data: {
    sessionId: string;
    step: number;
    stepName: string;
    status: string;
    error?: string;
    details?: string;
  }): void {
    const session = this.sessions.get(data.sessionId);
    if (!session) return;

    this.log('go', data.status === 'failed' ? 'error' : 'info', data.step,
      `${data.stepName}: ${data.status}${data.details ? ` - ${data.details}` : ''}${data.error ? ` (${data.error})` : ''}`);

    this.updateStep(
      session.attempt,
      data.step,
      data.status as StepStatus['status'],
      data.error,
      data.details
    );

    // 处理失败
    if (data.status === 'failed') {
      // 防御：如果该 session 已在 establishedConnections（已 handleSessionSuccess），
      // 忽略 Host 发来的迟到 failed 消息（常见于 quality_result 双发场景）。
      if (this.establishedConnections.has(data.sessionId)) {
        this.log('web', 'warning', data.step,
          `忽略迟到的 failed 消息: session 已 established (${data.error || 'unknown'})`);
        return;
      }
      this.handleSessionFailed(session, data.step, data.error || '未知错误', 'go');
    }

    // 检查是否成功完成
    if (data.step === Steps.COMPLETE && data.status === 'success') {
      this.handleSessionSuccess(session);
    }

    this.emitStateChange();
  }

  // 处理质量测试进度
  private handleQualityProgress(data: { sessionId: string; chunkIndex: number; totalChunks: number; totalBytes: number }): void {
    const session = this.sessions.get(data.sessionId);
    if (!session) return;

    this.updateStep(session.attempt, Steps.QUALITY_TEST_SEND, 'running', undefined,
      `发送中 ${data.chunkIndex}/${data.totalChunks}`);
    this.emitStateChange();
  }

  // 处理质量测试包（通过不可靠quality-test通道接收）
  private handleQualityPacket(session: AssistSession, data: ArrayBuffer | string): void {
    // 防重入：已完成质量测试后，迟到包直接忽略，避免重新初始化 qualityTest 导致二次结算
    if (session.qualityTestCompleted) return;

    // 解析包数据
    let seq = -1;
    let total = 3072; // 默认总包数 (3MB / 1KB)

    try {
      const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
      // 只解析JSON头部分
      const jsonEnd = text.indexOf('}');
      if (jsonEnd > 0) {
        const json = JSON.parse(text.substring(0, jsonEnd + 1));
        seq = json.seq;
        total = json.total || total;
      }
    } catch {
      // 解析失败，忽略此包
      return;
    }

    if (seq < 0) return;

    // 初始化质量测试
    if (!session.qualityTest) {
      session.qualityTest = {
        startTime: Date.now(),
        bytesReceived: 0,
        packetsReceived: new Set<number>(),
        totalPackets: total,
      };
      this.updateStep(session.attempt, Steps.QUALITY_TEST_RECV, 'running', undefined, '开始接收测试数据');

      // 设置质量测试超时（30秒，因为要发3MB数据）
      session.qualityTestTimeout = window.setTimeout(() => {
        if (session.qualityTest && session.attempt.status === 'running') {
          this.log('web', 'warning', Steps.QUALITY_TEST_RECV,
            `质量测试超时 (收到 ${session.qualityTest.packetsReceived.size}/${session.qualityTest.totalPackets} 包)`);
          if (session.qualityTest.packetsReceived.size > 0) {
            this.completeQualityTest(session);
          } else {
            this.handleSessionFailed(session, Steps.QUALITY_TEST_RECV, '质量测试超时，未收到数据');
          }
        }
      }, 30000);
    }

    // 记录收到的包
    const bytes = typeof data === 'string' ? data.length : data.byteLength;
    session.qualityTest.bytesReceived += bytes;
    session.qualityTest.packetsReceived.add(seq);
    session.qualityTest.totalPackets = total;

    const received = session.qualityTest.packetsReceived.size;

    // 每500包打印一次日志
    if (received % 500 === 0 || received === 1) {
      const mb = (session.qualityTest.bytesReceived / 1024 / 1024).toFixed(1);
      this.log('web', 'info', Steps.QUALITY_TEST_RECV,
        `收到 ${received}/${total} 包 (${mb}MB)`);
    }
    this.updateStep(session.attempt, Steps.QUALITY_TEST_RECV, 'running', undefined,
      `接收中 ${received}/${total} (${Math.round(received/total*100)}%)`);

    // 检查是否接收完成（收到最后一个包或超过95%的包）
    const maxSeq = Math.max(...session.qualityTest.packetsReceived);
    if (maxSeq >= total - 1 || received >= total * 0.95) {
      // 等待100ms让剩余的包到达
      setTimeout(() => {
        if (session.qualityTest && session.attempt.status === 'running') {
          this.completeQualityTest(session);
        }
      }, 100);
    }

    this.emitStateChange();
  }

  // 完成质量测试（计算吞吐量和丢包率）
  // 新增：与当前 turn 质量对比，只有 P2P 更优才触发真正的切换（Host 端看 skip_switch 字段）
  private async completeQualityTest(session: AssistSession): Promise<void> {
    // 幂等保护：
    // (1) qualityTestCompleted 标志立即置 true，阻止 handleQualityPacket 看到 null 后重新初始化 qualityTest。
    // (2) 旧逻辑的 qualityTest=null 也保留（防 await 期间被另一路径重入）。
    if (session.qualityTestCompleted) return;
    session.qualityTestCompleted = true;
    const qt = session.qualityTest;
    if (!qt) return;
    session.qualityTest = null;

    // 清除质量测试超时
    if (session.qualityTestTimeout) {
      clearTimeout(session.qualityTestTimeout);
      session.qualityTestTimeout = null;
    }

    const timeMs = Date.now() - qt.startTime;
    const packetsReceived = qt.packetsReceived.size;
    const totalPackets = qt.totalPackets;
    const packetsLost = totalPackets - packetsReceived;
    const lossRate = (packetsLost / totalPackets) * 100;
    const throughputKBps = Math.round(qt.bytesReceived / 1024 / (timeMs / 1000));

    // 通过条件：吞吐量>=500KB/s 且 丢包率<2%
    // ⚠️ 三端硬编码必须一致：webrtc_host.cpp + NewWeb + Flutter 都用 500
    const minThroughput = 500;
    const maxLossRate = 2.0;
    const passed = throughputKBps >= minThroughput && lossRate < maxLossRate;

    // 从 pc.getStats 获取 P2P 的 RTT + 客户端 winning local candidate 信息
    let p2pRttMs = 0;
    let localCandidateDesc = '';
    let remoteCandidateDesc = '';
    if (session.pc && passed) {
      try {
        const stats = await session.pc.getStats();
        let winningLocalId = '';
        let winningRemoteId = '';
        stats.forEach((report: RTCStats) => {
          if (report.type === 'candidate-pair') {
            const p = report as RTCIceCandidatePairStats;
            const rtt = p.currentRoundTripTime;
            if (p.state === 'succeeded' && p.nominated) {
              if (typeof rtt === 'number') p2pRttMs = rtt * 1000;
              winningLocalId = p.localCandidateId || '';
              winningRemoteId = p.remoteCandidateId || '';
            } else if (p.state === 'succeeded' && p2pRttMs === 0 && typeof rtt === 'number') {
              // fallback: 任一 succeeded 对的 RTT
              p2pRttMs = rtt * 1000;
              if (!winningLocalId) winningLocalId = p.localCandidateId || '';
              if (!winningRemoteId) winningRemoteId = p.remoteCandidateId || '';
            }
          }
        });
        // 查找 winning local/remote candidate 的地址/STUN url
        stats.forEach((report: RTCStats) => {
          const r = report as RTCIceCandidatePairStats & { id?: string; address?: string; port?: number; url?: string; candidateType?: string; type: string };
          if (winningLocalId && r.id === winningLocalId) {
            const stunLabel = r.url ? `STUN(${r.url.replace(/^stun:/, '')})` : (r.candidateType || 'local');
            localCandidateDesc = `${stunLabel} → ${r.address}:${r.port} [${r.candidateType || '?'}]`;
          }
          if (winningRemoteId && r.id === winningRemoteId) {
            // 浏览器看不到 remote candidate 的 url（只有 Host 知道），此处先用 candidateType 占位
            // 后续会被 p2p_assist:host_candidate_info 事件覆盖为带 STUN url 的完整字符串
            remoteCandidateDesc = `→ ${r.address}:${r.port} [${r.candidateType || '?'}]`;
          }
        });
      } catch (e) {
        this.log('web', 'warning', undefined, `获取 P2P stats 失败: ${(e as Error).message}`);
      }
    }

    this.updateStep(session.attempt, Steps.QUALITY_TEST_RECV, 'success', undefined,
      `收到 ${packetsReceived}/${totalPackets} 包`);

    this.updateStep(session.attempt, Steps.QUALITY_RESULT, 'running');

    const resultMsg = `吞吐量: ${throughputKBps}KB/s, 丢包: ${lossRate.toFixed(2)}%, RTT: ${p2pRttMs.toFixed(0)}ms`;
    if (passed) {
      this.log('web', 'success', Steps.QUALITY_RESULT, `${resultMsg} - 通过`);
      this.updateStep(session.attempt, Steps.QUALITY_RESULT, 'success', undefined, resultMsg);
    } else {
      const failReason = [];
      if (throughputKBps < minThroughput) failReason.push(`吞吐量不足(${throughputKBps}<${minThroughput}KB/s)`);
      if (lossRate >= maxLossRate) failReason.push(`丢包率过高(${lossRate.toFixed(2)}%>=${maxLossRate}%)`);
      this.log('web', 'error', Steps.QUALITY_RESULT, `${resultMsg} - 不通过: ${failReason.join(', ')}`);
      this.updateStep(session.attempt, Steps.QUALITY_RESULT, 'failed', failReason.join(', '), resultMsg);
    }

    // 新架构：凡是 passed 就加入 established 候选列表；"比 TURN 更优"的比较由 findBestEstablished 内部排序处理。
    // 因此 skip_switch 永远为 false —— 让 Host 把所有通过质量测试的 session 都标记 quality_passed=true，
    // 保护其不被后续 attempt 的 Fix A 清理。
    const skipSwitch = false;
    session.attempt.skipSwitch = skipSwitch;

    signalingService.send({
      type: 'p2p_assist',
      action: 'quality_result',
      sessionId: session.id,
      packetsReceived,
      packetsLost,
      lossRate,
      throughputKBps,
      timeMs,
      skip_switch: skipSwitch,
    });

    // 记录结果
    session.attempt.result = {
      connectionType: 'p2p',
      throughputKbps: throughputKBps * 8, // 转换为kbps用于显示
      rttMs: p2pRttMs || undefined,
      totalTimeMs: Date.now() - session.attempt.startTime,
      localCandidate: localCandidateDesc || undefined,
      remoteCandidate: remoteCandidateDesc || undefined,
    };

    // 新架构：只要质量测试通过就加入 establishedConnections（不再要求"比 TURN 更优"）
    // "比 TURN 更优"的判定在 findBestEstablished 里用吞吐/RTT 排序时体现，
    // 不再用于过滤哪些 session 加入 established。
    if (passed) {
      this.handleSessionSuccess(session);
    } else {
      session.attempt.status = 'failed';
      session.attempt.failedReason = `质量不达标`;
      session.attempt.failedStep = Steps.QUALITY_RESULT;
      session.attempt.failedSide = 'web';
      session.attempt.endTime = Date.now();
      // 打洞报告：质量不达标也是失败，采集诊断
      void this.finalizeAttemptDiagnostics(session);
      this.currentAttemptIndex++;
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = window.setTimeout(() => {
        this.tryNextStunServer();
      }, 500);
    }
  }

  // 处理会话成功（累积选优模式）
  //
  // 新策略：
  //   1. 不再早停，session 成功后加入 establishedConnections，继续跑下一个 attempt
  //   2. 不弹窗，改发气泡（debounce）通知"已有 N 条可用 P2P"
  //   3. established 达到 AUTO_SWITCH_THRESHOLD 时，自动选最优并 prepare+切换
  //   4. Host 端收到 quality_result 不立即加视频轨，等浏览器发 prepare
  //
  // 幂等保护：本方法会被两个路径调用——
  //   (a) completeQualityTest 本地判定通过后直接调
  //   (b) Host 回传 step_update(COMPLETE, success) 经 handleStepUpdate 触发
  // 若不去重，currentAttemptIndex 会被 +2，导致快速越界 onAllAttemptsComplete → isRunning=false 停跑
  private handleSessionSuccess(session: AssistSession): void {
    if (this.establishedConnections.has(session.id)) {
      // 已处理过（前一次从另一路径到达），忽略重复触发
      return;
    }
    session.attempt.status = 'success';
    session.attempt.endTime = Date.now();

    // 打洞报告：成功后异步采 finalStats + diagnose（PC 还活着，几百 ms 内完成）
    void this.finalizeAttemptDiagnostics(session);

    this.updateStep(session.attempt, Steps.COMPLETE, 'success', undefined,
      `P2P成功! 耗时${session.attempt.result?.totalTimeMs || 0}ms`);

    this.log('web', 'success', Steps.COMPLETE,
      `#${session.attempt.index + 1}完成: P2P成功, 吞吐${session.attempt.result?.throughputKbps || 0}kbps`);

    // 记录已建立的P2P连接
    if (session.pc) {
      const established: EstablishedP2PConnection = {
        sessionId: session.id,
        stunServers: session.stunServers,
        connectionType: session.attempt.result?.connectionType || 'p2p',
        throughputKbps: session.attempt.result?.throughputKbps || 0,
        rttMs: session.attempt.result?.rttMs,
        localCandidate: session.attempt.result?.localCandidate,
        remoteCandidate: session.attempt.result?.remoteCandidate,
        establishedAt: Date.now(),
        iceState: session.pc.iceConnectionState,
        pc: session.pc,
        hasVideo: !!session.remoteStream?.getVideoTracks().length,
        hasAudio: !!session.remoteStream?.getAudioTracks().length,
        // 新字段
        ready: false,                      // 视频轨未加，等 prepare → switch_ready 置 true
        acceptedAt: Date.now(),
        attempt: session.attempt,
      };
      this.establishedConnections.set(session.id, established);

      // 监听连接状态变化：断开时从 established 移除并重算 best
      session.pc.oniceconnectionstatechange = () => {
        const conn = this.establishedConnections.get(session.id);
        if (conn && session.pc) {
          const iceState = session.pc.iceConnectionState;
          conn.iceState = iceState;
          if (iceState === 'disconnected' || iceState === 'failed') {
            this.log('web', 'warning', undefined, `P2P连接断开: ${session.stunServers.join(', ')}`);
            // 给历史 attempt 记一笔"建立后掉线"，供"被过滤的 P2P 列表"展示
            session.attempt.droppedAfterEstablished = true;
            session.attempt.droppedReason = iceState === 'failed' ? 'ICE failed' : 'ICE disconnected';
            session.attempt.droppedAt = Date.now();
            this.establishedConnections.delete(session.id);
            this.updateBestFromEstablished();  // 断开后重算 best，保持 UI 一致
            this.emitStateChange();
          }
        }
      };
    }

    // bestAttempt 永远派生自 established 中的最优（统一信息源）
    this.updateBestFromEstablished();
    this.emitStateChange();

    // 气泡提示（debounce 合并）
    this.scheduleAssistToast();

    // 首条 established 出现 → 启动 60s 强制切换倒计时（仅启动一次）
    if (!this.firstEstablishedCountdownTimer && this.firstEstablishedAt === null) {
      this.firstEstablishedAt = Date.now();
      this.log('web', 'info', undefined,
        `首条 P2P 打通，启动 ${this.FORCE_SWITCH_TIMEOUT_MS / 1000}s 强制切换倒计时`);
      this.firstEstablishedCountdownTimer = window.setTimeout(
        () => this.forceAutoSwitch(),
        this.FORCE_SWITCH_TIMEOUT_MS,
      );
    }

    // 阈值触发：自动切到最优（扳机收权：上报 PathObserver 决策）
    if (this.establishedConnections.size >= this.AUTO_SWITCH_THRESHOLD) {
      const best = this.findBestEstablished();
      if (best) {
        this.log('web', 'success', undefined,
          `已建立 ${this.establishedConnections.size} 条 P2P 连接，自动切换到最优: ${best.stunServers.join(', ')} (${best.throughputKbps}kbps)`);
        this.clearForceSwitchCountdown();
        this.delegateAutoSwitch(best.sessionId);
        return;   // 不再 tryNextStunServer
      }
    }

    // 未到阈值：继续跑下一个 attempt
    this.currentAttemptIndex++;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.tryNextStunServer();
    }, this.STUN_RETRY_DELAY);
  }

  // 60s 到点强制切换：即使只有 1 条 established，也用当前最优切换，避免一直等不到第 3 条
  private forceAutoSwitch(): void {
    this.firstEstablishedCountdownTimer = null;
    if (!this.isRunning) return;
    const best = this.findBestEstablished();
    if (!best) {
      // established 全部断开，清空 firstEstablishedAt 允许新 established 重新启动倒计时
      this.firstEstablishedAt = null;
      this.log('web', 'warning', undefined, '强制切换倒计时到，但当前无可用 established（全部断开），继续尝试');
      this.emitStateChange();
      return;
    }
    this.log('web', 'success', undefined,
      `强制切换倒计时到（60s），用当前最优切换 (${this.establishedConnections.size} 条候选): ${best.stunServers.join(', ')} (${best.throughputKbps}kbps)`);
    this.delegateAutoSwitch(best.sessionId);
  }

  private clearForceSwitchCountdown(): void {
    if (this.firstEstablishedCountdownTimer) {
      clearTimeout(this.firstEstablishedCountdownTimer);
      this.firstEstablishedCountdownTimer = null;
    }
    this.firstEstablishedAt = null;
  }

  // ===== 累积选优：派生 bestAttempt 与选最优 =====
  // 规则：吞吐高 > RTT 低 > 先加入者（acceptedAt 小）
  private findBestEstablished(): EstablishedP2PConnection | null {
    let best: EstablishedP2PConnection | null = null;
    for (const c of this.establishedConnections.values()) {
      if (!best) { best = c; continue; }
      const t1 = best.throughputKbps, t2 = c.throughputKbps;
      const r1 = best.rttMs ?? Infinity, r2 = c.rttMs ?? Infinity;
      if (t2 > t1) { best = c; continue; }
      if (t2 === t1 && r2 < r1) { best = c; continue; }
      if (t2 === t1 && r2 === r1 && c.acceptedAt < best.acceptedAt) { best = c; continue; }
    }
    return best;
  }

  private updateBestFromEstablished(): void {
    const best = this.findBestEstablished();
    this.bestAttempt = best ? best.attempt : null;
  }

  // 气泡提示：每次有新 P2P 打通时提示用户，debounce 1s 合并多次
  private scheduleAssistToast(): void {
    this.notifyPendingCount++;
    if (this.notifyToastTimer) return;
    this.notifyToastTimer = window.setTimeout(() => {
      const n = this.notifyPendingCount;
      this.notifyPendingCount = 0;
      this.notifyToastTimer = null;
      const prefix = n > 1 ? `已有 ${n} 条可用的 P2P 链接` : '已有可用的 P2P 链接';
      eventBus.emit('ui:toast', {
        message: `${prefix}，打开"P2P竞速面板"可主动切换，也可等待最优 P2P 自动切换`,
        type: 'info',
      });
    }, this.TOAST_DEBOUNCE_MS);
  }

  // 请求切换到指定 session：若 ready 直接切，否则发 prepare 请求，等 Host 完成视频轨加载
  private prepareAndSwitch(sessionId: string): void {
    const conn = this.establishedConnections.get(sessionId);
    if (!conn) {
      this.log('web', 'error', undefined, `prepareAndSwitch: 找不到 session ${sessionId}`);
      return;
    }

    // 竞争保护：多次点击或自动/手动并发时，只处理最后一次
    this.pendingPrepareSessionId = sessionId;

    if (conn.ready) {
      // 视频轨已就绪，直接切
      this.log('web', 'info', undefined, `${sessionId} 已 ready，直接切换`);
      this._executeSwitchById(sessionId);
      return;
    }

    // 发 prepare 请求（Host 收到后会 AddVideoTrack + 重协商，完成后发 switch_ready）
    this.log('web', 'info', undefined, `发送 prepare 请求: ${sessionId}`);
    signalingService.send({
      type: 'p2p_assist',
      action: 'prepare',
      sessionId,
    });

    // 超时保护
    if (this.prepareTimeoutTimer) clearTimeout(this.prepareTimeoutTimer);
    this.prepareTimeoutTimer = window.setTimeout(() => {
      if (this.pendingPrepareSessionId === sessionId) {
        this.pendingPrepareSessionId = null;
        this.log('web', 'error', undefined, `prepare 超时: ${sessionId}`);
        eventBus.emit('ui:toast', {
          message: '切换请求超时，请稍后重试或检查 Host 版本',
          type: 'error',
        });
      }
      this.prepareTimeoutTimer = null;
    }, this.PREPARE_TIMEOUT_MS);
  }

  // 自动切换扳机收权（docs/path_observer_design.md §4.5）：不再直接 prepareAndSwitch，
  // emit 'path:p2p_ready' 交 PathObserver 决策（防护检查后回调 performSwitchTo 扣扳机，
  // pm 迁移在途时被抢占 abort）。不 import pathObserver，避免与 PathObserver→本服务的循环依赖。
  private delegateAutoSwitch(sessionId: string): void {
    console.log(`[P2PAssist] p2p ready, delegating switch decision to PathObserver: session=${sessionId}`);
    this.log('web', 'info', undefined, `p2p ready，上报 PathObserver 决策切换: ${sessionId}`);
    eventBus.emit('path:p2p_ready', { sessionId });
  }

  // 公开：用户点击"切换"按钮切到指定 P2P 连接（手动意愿直通，不经 PathObserver；
  // PathObserver 批准自动切换后也经此入口回调扣扳机）
  performSwitchTo(sessionId: string): void {
    this.prepareAndSwitch(sessionId);
  }

  // 公开只读查询：该 session 是否仍在已建立连接池。PathObserver 选 p2p 目标前验活，
  // 防止选中已被 host 关闭的缓存 session（连接断开时 oniceconnectionstatechange 会
  // 从 establishedConnections 移除，本查询即反映存活状态）
  hasEstablished(sessionId: string): boolean {
    return this.establishedConnections.has(sessionId);
  }

  // 根据 sessionId 从 sessions/established 组装切换上下文并执行
  private _executeSwitchById(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    const conn = this.establishedConnections.get(sessionId);
    if (!session || !session.pc || !conn) {
      this.log('web', 'error', undefined, `_executeSwitchById: session 或 established 缺失: ${sessionId}`);
      return;
    }
    if (!session.remoteStream) {
      this.log('web', 'error', undefined, `_executeSwitchById: remoteStream 为空: ${sessionId}`);
      return;
    }
    const ctx: PendingSwitchContext = {
      sessionId,
      pc: session.pc,
      dataChannel: session.dataChannel,
      inputChannel: session.inputChannel,
      mouseChannel: session.mouseChannel,
      remoteStream: session.remoteStream,
      stunServers: session.stunServers,
      connectionType: conn.connectionType,
    };
    this.pendingPrepareSessionId = null;
    this._executeSwitch(ctx);
  }

  // 处理会话失败（async：先采集 stats 再 cleanup，保证打洞报告能拿到 selectedPair / allPairs）
  private async handleSessionFailed(session: AssistSession, failedStep: number, reason: string, side: 'web' | 'go' = 'web'): Promise<void> {
    // 幂等保护：attempt 已被先前路径判 failed（如 completeQualityTest 失败分支），
    // 此后到达的二次失败回调（典型：host step_update step=COMPLETE failed）直接忽略，
    // 避免覆盖首次的 failedReason / failedStep / failedSide / diagnosis，
    // 也避免 currentAttemptIndex 被重复 ++ 跳过下一个 attempt。
    if (session.attempt.status === 'failed') {
      this.log('web', 'warning', failedStep,
        `handleSessionFailed 重入忽略（attempt 已 failed），原因: ${reason}`);
      return;
    }

    session.attempt.status = 'failed';
    session.attempt.endTime = Date.now();
    session.attempt.failedStep = failedStep;
    session.attempt.failedSide = side;
    session.attempt.failedReason = reason;

    this.updateStep(session.attempt, failedStep, 'failed', reason);

    // 标记后续步骤为跳过（含 Sync 扩展步骤 17-20）
    // 注意：step ID 数字顺序与实际执行顺序不一致——Sync 协同 (17/18/19) 在 ICE (10/11) 之前完成。
    // 因此 ICE 失败时不能粗暴把 12..20 全标 skipped，否则会覆盖已 success 的 Sync 步骤。
    for (let i = failedStep + 1; i <= 20; i++) {
      const existing = session.attempt.steps.find(s => s.step === i);
      if (!existing || (existing.status !== 'success' && existing.status !== 'skipped' && existing.status !== 'failed')) {
        this.updateStep(session.attempt, i, 'skipped');
      }
    }

    // Sync 定时器取消（防止已失败 session 仍在等 fire）
    if (session.syncFireTimer !== null) {
      clearTimeout(session.syncFireTimer);
      session.syncFireTimer = null;
    }

    this.log(side, 'error', failedStep, `失败: ${reason}`);

    // 打洞报告：在 cleanup 之前先采集 finalStats + 拷贝 syncInfo + 诊断（PC 还在）
    try {
      await this.collectFinalStats(session);
    } catch { /* swallow */ }
    const diag = this.getDiag(session.attempt);
    if (!diag.syncSnapshot && session.syncInfo) {
      diag.syncSnapshot = { ...session.syncInfo };
    }
    this.diagnose(session.attempt);

    // 清理会话（若在 establishedConnections 中也要移除并重算 best）
    const wasEstablished = this.establishedConnections.has(session.id);
    this.cleanupSession(session);
    if (wasEstablished) {
      this.establishedConnections.delete(session.id);
      this.updateBestFromEstablished();
    }

    this.emitStateChange();

    // 继续尝试下一个STUN服务器
    this.currentAttemptIndex++;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.tryNextStunServer();
    }, this.STUN_RETRY_DELAY);
  }

  // 统一入口取消 connectTimeout —— 任意"链路活着"事件命中即可清除
  // (iceConnectionState=connected/completed | pc.connectionState=connected | dc.onopen | qualityChannel.onopen)
  // 修复 bug：原本只在 iceConnectionState 切到 connected 时清，但该状态机相对 DC.onopen
  // 有几十~几百 ms 异步滞后，3s 兜底 timer 可能在 DC 已 open 收到首个质量包之后才到期，
  // 导致已建立的 attempt 被误杀为"ICE连接超时"。
  private clearConnectTimeout(session: AssistSession, _reason: string): void {
    if (session.connectTimeout) {
      clearTimeout(session.connectTimeout);
      session.connectTimeout = null;
    }
  }

  // 清理会话（释放 DataChannel / PC / stream 所占资源）
  private cleanupSession(session: AssistSession, keepEstablished = false): void {
    if (session.connectTimeout) {
      clearTimeout(session.connectTimeout);
      session.connectTimeout = null;
    }
    if (session.qualityTestTimeout) {
      clearTimeout(session.qualityTestTimeout);
      session.qualityTestTimeout = null;
    }
    if (session.statsPollTimer !== null) {
      clearInterval(session.statsPollTimer);
      session.statsPollTimer = null;
    }

    // 先显式关闭 DataChannel，再关 PC，避免 native 层残留回调
    const closeChannel = (ch: RTCDataChannel | null): void => {
      if (!ch) return;
      try {
        ch.onmessage = null;
        ch.onopen = null;
        ch.onclose = null;
        ch.onerror = null;
        if (ch.readyState !== 'closed' && ch.readyState !== 'closing') {
          ch.close();
        }
      } catch {}
    };
    closeChannel(session.dataChannel);
    closeChannel(session.qualityChannel);
    closeChannel(session.inputChannel);
    closeChannel(session.mouseChannel);
    closeChannel(session.fileCtrlChannel);
    closeChannel(session.fileDataChannel);
    session.dataChannel = null;
    session.qualityChannel = null;
    session.inputChannel = null;
    session.mouseChannel = null;
    session.fileCtrlChannel = null;
    session.fileDataChannel = null;

    // 停止 remoteStream 上的所有 track
    if (session.remoteStream) {
      try {
        for (const track of session.remoteStream.getTracks()) {
          track.stop();
        }
      } catch {}
      session.remoteStream = null;
    }

    if (session.pc) {
      // 先清除事件处理器，避免关闭时触发状态变化导致UI闪烁
      session.pc.oniceconnectionstatechange = null;
      session.pc.onicecandidate = null;
      session.pc.ontrack = null;
      session.pc.ondatachannel = null;
      session.pc.onconnectionstatechange = null;
      try {
        session.pc.close();
      } catch {}
      session.pc = null;
    }

    this.sessions.delete(session.id);
    if (!keepEstablished) {
      this.establishedConnections.delete(session.id);
    }
  }

  /**
   * 统一资源清理入口
   *
   * 所有"结束 P2P Assist 或结束一轮"的出口点都必须调用此方法，保证
   * 未使用的 PC / DataChannel / MediaStream / Timer 全部释放，不残留引用。
   */
  private finalizeResources(mode: FinalizeMode): void {
    // 共享定时器
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (mode !== FinalizeMode.KeepBestForSwitch) {
      if (this.switchReadyTimeout) {
        clearTimeout(this.switchReadyTimeout);
        this.switchReadyTimeout = null;
      }
    }

    const keepId =
      mode === FinalizeMode.KeepBestForSwitch ? this.bestAttempt?.sessionId ?? null : null;

    // 清理 sessions
    const sessionIds = Array.from(this.sessions.keys());
    for (const id of sessionIds) {
      if (keepId !== null && id === keepId) continue;
      const s = this.sessions.get(id);
      if (s) {
        this.cleanupSession(s);
      }
    }

    // 清理 establishedConnections（兜底：关 session 已 cleanup 但 established 残留的 PC）
    const establishedIds = Array.from(this.establishedConnections.keys());
    for (const id of establishedIds) {
      if (keepId !== null && id === keepId) continue;
      const conn = this.establishedConnections.get(id);
      if (conn?.pc) {
        try {
          conn.pc.oniceconnectionstatechange = null;
          conn.pc.onicecandidate = null;
          conn.pc.ontrack = null;
          conn.pc.ondatachannel = null;
          conn.pc.onconnectionstatechange = null;
          conn.pc.close();
        } catch {}
      }
      this.establishedConnections.delete(id);
    }

    // 标记剩余未执行的 attempt 为 skipped（UI 反映早停/结束）
    for (const attempt of this.attempts) {
      if (attempt.status === 'pending') {
        attempt.status = 'failed';
        if (!attempt.failedReason) {
          attempt.failedReason =
            mode === FinalizeMode.KeepBestForSwitch ? 'early_stop: winner_found' : 'aborted';
        }
        attempt.endTime = Date.now();
        // 打洞报告：兜底走一次 diagnose（无 finalStats，仅根据已采集的 diag 判断）
        if (!attempt.diagnosis) this.diagnose(attempt);
        for (let i = 1; i <= 16; i++) {
          this.updateStep(attempt, i, 'skipped');
        }
      }
    }

    if (mode !== FinalizeMode.KeepBestForSwitch) {
      this.bestAttempt = null;
      this.pendingSwitch = null;
    }

    if (mode === FinalizeMode.DiscardAll) {
      this.turnRttHistory = [];
      this.turnLossHistory = [];
    }

    this.emitStateChange();
  }

  /**
   * 早停：当前 attempt 明显优于 TURN，跳过剩余 attempt，提前结束本轮
   *
   * winner 已在 handleSessionSuccess 里被存入 bestAttempt 和 establishedConnections，
   * 这里只需清理其他资源并走"一轮完成有 best"分支。
   */
  private stopRoundEarly(winner: AssistSession): void {
    const remaining = Math.max(0, this.attempts.length - this.currentAttemptIndex - 1);
    this.log(
      'web',
      'success',
      undefined,
      `早停: #${winner.attempt.index + 1} 明显优于 TURN, 跳过剩余 ${remaining} 个 attempt`,
    );

    // 标记所有后续 attempt 已完成
    this.currentAttemptIndex = this.attempts.length;

    // 清理非 best 的所有资源
    this.finalizeResources(FinalizeMode.KeepBestForSwitch);

    // 走原"一轮完成有 best"分支（等 Host 的 switch_ready）
    this.onAllAttemptsComplete();
  }

  // 所有尝试完成
  private onAllAttemptsComplete(): void {
    const estCount = this.establishedConnections.size;

    // 达到阈值（兜底，正常由 handleSessionSuccess 即时触发）
    if (estCount >= this.AUTO_SWITCH_THRESHOLD) {
      const best = this.findBestEstablished();
      if (best) {
        this.log('web', 'success', undefined, `轮末发现 ${estCount} 条已达阈值，切换到最优`);
        this.clearForceSwitchCountdown();
        this.delegateAutoSwitch(best.sessionId);
      }
      return;
    }

    // 新策略：未达阈值 → 继续下一轮，保留已 established（不调 finalizeResources 避免清理）
    // 只重置 currentAttemptIndex 和 attempts 数组供下一轮使用
    if (this.roundCount < this.MAX_ROUNDS) {
      const delay = this.roundCount * this.BASE_RETRY_DELAY;
      const msg = estCount > 0
        ? `第${this.roundCount}轮完成，已建立 ${estCount} 条 P2P（未达 ${this.AUTO_SWITCH_THRESHOLD} 条阈值），${delay / 1000}秒后开始第${this.roundCount + 1}轮继续打洞`
        : `第${this.roundCount}轮未找到P2P，${delay / 1000}秒后开始第${this.roundCount + 1}轮`;
      this.log('web', 'info', undefined, msg);
      this.emitStateChange();

      this.retryTimer = window.setTimeout(() => {
        if (this.isRunning) {
          this.startNewRound();
        }
      }, delay);
    } else {
      this.isRunning = false;
      this.clearForceSwitchCountdown();
      this.log('web', 'error', undefined,
        `已尝试${this.MAX_ROUNDS}轮，共建立 ${estCount} 条 P2P，停止打洞（用户可手动从列表选择切换）`);
      this.emitStateChange();
    }
  }

  // 处理 Host 端重协商 Offer（质量测试通过后 Host 添加视频轨道触发）
  private async handleRenegotiateOffer(data: { raceId: string; sdp: string }): Promise<void> {
    // raceId 格式: "p2p-assist-{sessionId}"
    const sessionId = data.raceId.replace('p2p-assist-', '');
    this.log('web', 'info', undefined, `收到重协商 Offer: ${sessionId}`);

    // 从 sessions 或 establishedConnections 找到对应的 PeerConnection
    const session = this.sessions.get(sessionId);
    const established = this.establishedConnections.get(sessionId);
    const pc = session?.pc || established?.pc;

    if (!pc) {
      this.log('web', 'error', undefined, `重协商失败: 找不到 session ${sessionId} 的 PeerConnection`);
      return;
    }

    try {
      // 设置远端描述（Host 的新 Offer，包含视频轨道）
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: data.sdp }));
      this.log('web', 'info', undefined, `重协商: setRemoteDescription 成功`);

      // 创建 Answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.log('web', 'info', undefined, `重协商: createAnswer 成功`);

      // 通过标准信令通道发送 Answer（带 raceId 以便 Host 路由到正确的 PeerSession）
      signalingService.sendAnswer(answer.sdp!, data.raceId);
      this.log('web', 'success', undefined, `重协商 Answer 已发送: ${data.raceId}`);

      // 检查 ontrack 是否已触发（视频轨道应该在 setRemoteDescription 后可用）
      const receivers = pc.getReceivers();
      const videoReceivers = receivers.filter(r => r.track?.kind === 'video');
      this.log('web', 'info', undefined, `重协商后: ${videoReceivers.length} 个视频 receiver`);

      // 确保 remoteStream 更新
      if (session && !session.remoteStream) {
        session.remoteStream = new MediaStream();
      }
      if (session?.remoteStream) {
        videoReceivers.forEach(r => {
          if (r.track && !session.remoteStream!.getTrackById(r.track.id)) {
            session.remoteStream!.addTrack(r.track);
            this.log('web', 'info', undefined, `重协商: 添加 ${r.track.kind} track 到 remoteStream`);
          }
        });
      }
    } catch (e) {
      this.log('web', 'error', undefined, `重协商失败: ${(e as Error).message}`);
    }
  }

  // 处理切换就绪（新架构）：标记对应 established.ready=true；
  // 若此 sessionId 是当前 pendingPrepareSessionId（用户已点击或自动触发切换），执行 _executeSwitchById；
  // 否则仅标记为 ready（对应 session 视频轨已就绪，可被切换按钮立即使用）。
  private handleSwitchReady(data: { sessionId: string }): void {
    this.log('web', 'success', undefined, `switch_ready 到达: ${data.sessionId}`);

    // 标记对应 established 的 ready 状态
    const conn = this.establishedConnections.get(data.sessionId);
    if (conn) {
      conn.ready = true;
      this.emitStateChange();
    }

    // 竞争保护：只处理当前正在等待的 sessionId（其它 switch_ready 仅标记 ready，不执行切换）
    if (this.pendingPrepareSessionId !== data.sessionId) {
      this.log('web', 'info', undefined,
        `switch_ready 非当前等待目标（pending=${this.pendingPrepareSessionId}），仅标记 ready`);
      return;
    }

    if (this.prepareTimeoutTimer) {
      clearTimeout(this.prepareTimeoutTimer);
      this.prepareTimeoutTimer = null;
    }

    this._executeSwitchById(data.sessionId);
  }

  // 执行实际切换（抽出核心逻辑供 acceptSwitch 和 _executeSwitchById 复用）
  private _executeSwitch(ctx: PendingSwitchContext): void {
    this.log('web', 'info', undefined, `执行切换到 P2P 连接 (${ctx.stunServers.join(', ')})`);

    // 把 best session 的 PC/channels 交给 WebRTCService
    eventBus.emit('p2p_assist:performSwitch', {
      sessionId: ctx.sessionId,
      pc: ctx.pc,
      dataChannel: ctx.dataChannel,
      inputChannel: ctx.inputChannel,
      mouseChannel: ctx.mouseChannel,
      remoteStream: ctx.remoteStream,
      stunServers: ctx.stunServers,
      connectionType: ctx.connectionType,
    });

    // 关键顺序：先把 best session 从 maps 中 remove（PC 已转交给 WebRTCService），
    // 然后 finalizeResources 才不会误关这个 PC 导致新连接立刻断。
    this.sessions.delete(ctx.sessionId);
    this.establishedConnections.delete(ctx.sessionId);
    this.pendingSwitch = null;

    this.mainConnectionType = 'p2p';
    this.isRunning = false;
    this.clearForceSwitchCountdown();

    // 统一清理剩余所有资源
    this.finalizeResources(FinalizeMode.DiscardAll);

    // 切换成功后只清 UI 临时态，保留 attempts/attemptHistory/logs/bestAttempt 供"打洞报告"按钮读取
    // 这些数据将在下次 start() 调用时统一清空（重新打洞 = 新报告）
    this.currentAttemptIndex = this.attempts.length; // 越界，UI 显示"等待开始"
    this.firstEstablishedAt = null;
    this.pendingPrepareSessionId = null;
    this.notifyPendingCount = 0;
    if (this.notifyToastTimer) { clearTimeout(this.notifyToastTimer); this.notifyToastTimer = null; }
    if (this.prepareTimeoutTimer) { clearTimeout(this.prepareTimeoutTimer); this.prepareTimeoutTimer = null; }
    this.emitStateChange();

    // 通知 Host 执行切换（关闭旧 TURN session，保留 P2P session）
    signalingService.send({
      type: 'p2p_assist',
      action: 'switch',
      sessionId: ctx.sessionId,
    });

    // 清理 Host 端其他辅助会话
    signalingService.send({
      type: 'p2p_assist',
      action: 'cleanup_others',
      sessionId: ctx.sessionId,
    });

    eventBus.emit('ui:toast', {
      message: `已切换到P2P连接 (${ctx.stunServers.join(', ')})`,
      type: 'success',
    });
  }

  // 保留兼容：原 pendingSwitch 弹窗流程（新架构下不会被触发，保留以防回退）
  acceptSwitch(): void {
    const ctx = this.pendingSwitch;
    if (!ctx) {
      this.log('web', 'warning', undefined, 'acceptSwitch 调用但无待处理切换');
      return;
    }
    this._executeSwitch(ctx);
  }

  // 用户点击"取消"时调用：通知 Host 关闭 standby session，本次连接不再尝试
  rejectSwitch(): void {
    const ctx = this.pendingSwitch;
    if (!ctx) {
      this.log('web', 'warning', undefined, 'rejectSwitch 调用但无待处理切换');
      return;
    }

    this.log('web', 'info', undefined, `用户取消切换 (${ctx.stunServers.join(', ')})，本次连接保持中转模式`);

    // 通知 Host 关闭 standby session（包括最佳 P2P session）
    signalingService.send({
      type: 'p2p_assist',
      action: 'stop',
      sessionId: ctx.sessionId,
    });

    // 停止整个辅助打洞流程（consumed 已置 true，本次连接不会再触发）
    this.isRunning = false;

    // 统一清理所有资源（含 pendingSwitch、bestAttempt、剩余 sessions/established）
    this.finalizeResources(FinalizeMode.DiscardAll);

    eventBus.emit('ui:toast', {
      message: '已保持中转模式',
      type: 'info',
    });
  }

  // 执行切换到最佳P2P连接（新架构：调 prepareAndSwitch 走 prepare → switch_ready → _executeSwitch 流程）
  performSwitch(): void {
    const best = this.findBestEstablished();
    if (!best) {
      this.log('web', 'error', undefined, '没有可用的P2P连接');
      return;
    }
    this.log('web', 'info', undefined, `请求切换到最优: ${best.stunServers.join(', ')}`);
    this.prepareAndSwitch(best.sessionId);
  }

  // 查找可用的P2P连接（优先bestAttempt，然后fallback到其他）
  private findAvailableConnection(): { sessionId: string; pc: RTCPeerConnection; stunServers: string[] } | null {
    // 首先尝试最佳会话
    if (this.bestAttempt) {
      const result = this.checkConnection(this.bestAttempt.sessionId);
      if (result) {
        return { ...result, stunServers: this.bestAttempt.stunServers };
      }
      this.log('web', 'warning', undefined, `最佳会话 ${this.bestAttempt.stunServers.join(', ')} 不可用，尝试其他连接`);
    }

    // Fallback: 从establishedConnections中找一个可用的
    for (const [sessionId, conn] of this.establishedConnections) {
      if (conn.pc &&
          conn.pc.iceConnectionState !== 'closed' &&
          conn.pc.iceConnectionState !== 'failed' &&
          conn.pc.iceConnectionState !== 'disconnected') {
        this.log('web', 'info', undefined, `找到备用连接: ${conn.stunServers.join(', ')}`);
        // 更新bestAttempt为这个可用的
        const attempt = this.attempts.find(a => a.sessionId === sessionId);
        if (attempt) {
          this.bestAttempt = attempt;
        }
        return { sessionId, pc: conn.pc, stunServers: conn.stunServers };
      }
    }

    // 再从sessions中找
    for (const [sessionId, session] of this.sessions) {
      if (session.pc &&
          session.pc.iceConnectionState !== 'closed' &&
          session.pc.iceConnectionState !== 'failed' &&
          session.pc.iceConnectionState !== 'disconnected' &&
          session.attempt.status === 'success') {
        this.log('web', 'info', undefined, `找到备用会话: ${session.stunServers.join(', ')}`);
        this.bestAttempt = session.attempt;
        return { sessionId, pc: session.pc, stunServers: session.stunServers };
      }
    }

    return null;
  }

  // 检查指定会话是否可用
  private checkConnection(sessionId: string): { sessionId: string; pc: RTCPeerConnection } | null {
    const session = this.sessions.get(sessionId);
    const established = this.establishedConnections.get(sessionId);
    const pc = session?.pc || established?.pc;

    if (!pc ||
        pc.iceConnectionState === 'closed' ||
        pc.iceConnectionState === 'failed' ||
        pc.iceConnectionState === 'disconnected') {
      return null;
    }

    return { sessionId, pc };
  }

  // 更新步骤状态
  private updateStep(
    attempt: StunAttempt,
    step: number,
    status: StepStatus['status'],
    error?: string,
    details?: string
  ): void {
    const stepStatus = attempt.steps.find(s => s.step === step);
    if (!stepStatus) return;

    if (status === 'running' && stepStatus.status === 'pending') {
      stepStatus.startTime = Date.now();
    }
    if (status === 'success' || status === 'failed') {
      stepStatus.endTime = Date.now();
    }

    stepStatus.status = status;
    if (error) stepStatus.error = error;
    if (details) stepStatus.details = details;
  }

  // 记录日志
  private log(side: 'web' | 'go', level: LogEntry['level'], step: number | undefined, message: string): void {
    const entry: LogEntry = {
      timestamp: Date.now(),
      side,
      level,
      step,
      message,
    };
    this.logs.push(entry);

    // 控制台输出已禁用（日志过多影响调试）；保留 this.logs 供面板回看
    // const prefix = `[P2PAssist][${side.toUpperCase()}]`;
    // if (level === 'error') console.error(prefix, message);
    // else if (level === 'warning') console.warn(prefix, message);
    // else console.log(prefix, message);

    // 限制日志数量
    if (this.logs.length > 1000) {
      this.logs = this.logs.slice(-500);
    }
  }

  // 发送状态变化事件
  private emitStateChange(): void {
    eventBus.emit('p2p_assist:stateChange', this.getState());
  }

  // 获取当前状态
  getState(): {
    isRunning: boolean;
    mainConnectionType: 'p2p' | 'relay' | 'unknown';
    currentAttemptIndex: number;
    totalStunServers: number;
    attempts: StunAttempt[];
    bestAttempt: StunAttempt | null;
    logs: LogEntry[];
    establishedConnections: EstablishedP2PConnection[];
    roundCount: number;
    maxRounds: number;
    syncInfo: SyncInfo | null;  // 当前 attempt 的 Sync 打洞状态（UI 用）
    firstEstablishedAt: number | null;      // 首条 established 时间戳（UI 倒计时用）
    forceSwitchTimeoutMs: number;           // 强制切换超时时长
    pendingPrepareSessionId: string | null; // 当前正在等 host switch_ready 的 session(N5 panel 切换按钮 pending 状态用)
    filteredAttempts: FilteredAttempt[];    // 被过滤的 P2P：质量不达标 / 各阶段失败 / 建立后断开
  } {
    // 更新已建立连接的状态，并过滤掉已关闭的连接
    const connections: EstablishedP2PConnection[] = [];
    const toDelete: string[] = [];

    this.establishedConnections.forEach((conn, id) => {
      try {
        // 检查PC是否还有效
        const iceState = conn.pc.iceConnectionState;
        if (iceState === 'closed') {
          // PC已关闭，标记删除
          toDelete.push(id);
        } else {
          conn.iceState = iceState;
          connections.push({ ...conn });
        }
      } catch {
        // PC可能已被销毁，标记删除
        toDelete.push(id);
      }
    });

    // 删除无效的连接记录
    toDelete.forEach(id => this.establishedConnections.delete(id));

    // 定位当前 attempt 对应的 session，读取 syncInfo 用于 UI 展示
    let syncInfo: SyncInfo | null = null;
    const curAttempt = this.attempts[this.currentAttemptIndex];
    if (curAttempt) {
      const curSession = this.sessions.get(curAttempt.sessionId);
      if (curSession) syncInfo = { ...curSession.syncInfo };
    }

    return {
      isRunning: this.isRunning,
      mainConnectionType: this.mainConnectionType,
      currentAttemptIndex: this.currentAttemptIndex,
      totalStunServers: ASSIST_STUN_SERVERS.length,
      attempts: [...this.attempts],
      bestAttempt: this.bestAttempt,
      logs: [...this.logs],
      establishedConnections: connections,
      roundCount: this.roundCount,
      maxRounds: this.MAX_ROUNDS,
      syncInfo,
      firstEstablishedAt: this.firstEstablishedAt,
      forceSwitchTimeoutMs: this.FORCE_SWITCH_TIMEOUT_MS,
      pendingPrepareSessionId: this.pendingPrepareSessionId,
      filteredAttempts: this.deriveFilteredAttempts(),
    };
  }

  // 从 attemptHistory 派生"被过滤的 P2P"列表（最近 50 条）
  // 排除：当前最佳、当前 running、完全没跑过的 pending
  private deriveFilteredAttempts(): FilteredAttempt[] {
    const bestSessionId = this.bestAttempt?.sessionId;
    const result: FilteredAttempt[] = [];
    for (const a of this.attemptHistory) {
      if (a.sessionId === bestSessionId) continue;
      if (a.status === 'running') continue;
      if (a.status === 'pending' && a.steps.every(s => s.status === 'pending')) continue;

      const isFailed = a.status === 'failed';
      const isDropped = a.droppedAfterEstablished === true;
      if (!isFailed && !isDropped) continue;

      let category: FilteredAttempt['filterCategory'] = 'other';
      let reason = '';
      let filteredAt = a.endTime ?? Date.now();

      // step QUALITY_RESULT 的状态是质量判定的最权威线索 —— Web 端 _completeQualityTest 写过 success/failed，
      // 即便 Host 后续 step_update step=COMPLETE failed 把 attempt.failedReason / failedStep / diagnosis
      // 二次覆盖，QUALITY_RESULT 这个 step 的 status 也不会被动到。优先用它判 quality。
      const qStep = a.steps.find(s => s.step === Steps.QUALITY_RESULT);
      const isQualityFail =
        qStep?.status === 'failed' ||
        a.failedReason === '质量不达标' ||
        /质量不达标|Quality test failed/i.test(a.failedReason || '');

      if (isDropped) {
        // 建立后断开优先级最高（即便后来又因别的原因 fail）
        category = 'dropped';
        reason = `建立后断开（${a.droppedReason || 'unknown'}）`;
        filteredAt = a.droppedAt ?? filteredAt;
      } else if (isQualityFail) {
        category = 'quality';
        // reason 优先用 step QUALITY_RESULT 的 error/details（含吞吐 / 丢包数字），
        // fallback 到 failedReason
        reason = qStep?.error || qStep?.details || a.failedReason || '质量不达标';
      } else if (a.diagnosis?.根因分类) {
        const root = a.diagnosis.根因分类;
        if (root === '信令错误') category = 'signal';
        else if (root === 'Sync探测失败') category = 'sync';
        else if (root === 'ICE超时' || root === 'NAT阻断' || root === 'STUN不可达' || root === 'STUN错误' || root === '远端STUN不可达') category = 'ice';
        else category = 'other';
        reason = `${root} · ${a.diagnosis.一句话结论 || a.failedReason || ''}`.trim();
      } else {
        reason = a.failedReason || '未知原因';
      }

      // 仅展示"质量不达标"和"建立后断开" —— 信令/ICE/Sync/其他失败数量太多，不放面板
      if (category !== 'quality' && category !== 'dropped') continue;

      result.push({ attempt: a, filterReason: reason, filterCategory: category, filteredAt });
    }
    // 按 filteredAt 倒序，最新的在前；切尾保留 50 条
    result.sort((x, y) => y.filteredAt - x.filteredAt);
    return result.slice(0, 50);
  }

  // 获取日志
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // 检查是否正在运行
  getIsRunning(): boolean {
    return this.isRunning;
  }
}

// 单例
export const p2pAssistService = new P2PAssistService();
export default p2pAssistService;
